import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  Download, 
  FileText, 
  Printer,
  Save,
  Edit2,
  Eye,
  AlertCircle,
  TrendingUp,
  Award,
  Activity,
  Mail,
  BookOpen,
  Info,
  Users,
  ClipboardList,
  CheckCircle,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';
import PrimaryReportCard from '@/components/PrimaryReportCard';
import SecondaryReportCard from '@/components/SecondaryReportCard';
import { getAssessmentTypeFromTitle, assessmentCategories } from '@/data/assessmentBlueprints';
import { classAssessmentStructure } from '../../data/classAssessments';

export default function GradeBookPageNew() {
  const currentUser = getCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const studentIdParam = searchParams.get('student');
  
  // Check if navigated from curriculum with assessment data
  const assessmentFromCurriculum = location.state?.assessmentFromCurriculum || null;
  const [showCurriculumBanner, setShowCurriculumBanner] = useState(!!assessmentFromCurriculum);
  const [curriculumAssessmentCreated, setCurriculumAssessmentCreated] = useState(false);
  const [schoolName, setSchoolName] = useState('Penama Education Portal');
  const [schoolData, setSchoolData] = useState({ principal_name: '', address: '', phone: '' });

  useEffect(() => {
    const fetchSchoolInfo = async () => {
      if (currentUser?.school_id) {
        try {
          const API_URL = '';
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/api/schools/${currentUser.school_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setSchoolName(data.name || 'Penama Education Portal');
            setSchoolData({
              principal_name: data.principal_name || '',
              address: data.address || '',
              phone: data.phone || '',
              school_id: data.id || ''
            });
          }
        } catch (error) {
          console.error('Error fetching school:', error);
        }
      }
    };
    fetchSchoolInfo();
  }, [currentUser?.school_id]);

  const [selectedYear, setSelectedYear] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [students, setStudents] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [grades, setGrades] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [reportCardOpen, setReportCardOpen] = useState(false);
  const [selectedReportStudent, setSelectedReportStudent] = useState(null);
  const [selectedStudentForReport, setSelectedStudentForReport] = useState(null);
  const [blueprintOpen, setBlueprintOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [years, setYears] = useState([]);
  const [allClasses, setAllClasses] = useState([]); // Store all classes to filter subjects by year
  const [selectedClass, setSelectedClass] = useState(null);
  const [availableTeachers, setAvailableTeachers] = useState([]); // For principal: multiple teachers per subject
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState("Term 1"); // Default to Term 1
  const [batchEntryOpen, setBatchEntryOpen] = useState(false);
  const [batchEntryType, setBatchEntryType] = useState('quiz'); // quiz, homework, project, classwork, exam, presentation, observation
  const [batchEntryStrand, setBatchEntryStrand] = useState('strand1'); // strand1, strand2, strand3, strand4, strand5
  const [batchGrades, setBatchGrades] = useState({});
  const [batchCustomName, setBatchCustomName] = useState(''); // Custom assessment name
  const [batchTotalScore, setBatchTotalScore] = useState(''); // Custom total score
  const [bulkPrintLoading, setBulkPrintLoading] = useState(false);
  const [compactView, setCompactView] = useState(true); // NEW: Toggle between compact (strand-based) and detailed view
  
  // State for fixed assessment marks (Quiz, Homework, Practical, Project, Test)
  const [assessmentMarks, setAssessmentMarks] = useState({});
  // State for exam marks (raw marks)
  const [examMarks, setExamMarks] = useState({});
  // State for exam total marks (teacher sets this)
  const [examTotalMarks, setExamTotalMarks] = useState(100);

  // Bulk Print All Report Cards Function
  const handleBulkPrintReportCards = async () => {
    if (students.length === 0) {
      toast.error('No students to print report cards for');
      return;
    }
    
    setBulkPrintLoading(true);
    toast.info(`Preparing ${students.length} report cards for printing...`);
    
    try {
      // Fetch report card data for all students
      const reportCardsData = [];
      
      for (const student of students) {
        try {
          const reportData = await apiService.getStudentReportCard(
            student.id,
            parseInt(selectedYear),
            selectedTerm
          );
          
          reportCardsData.push({
            student,
            reportData,
            caTotal: calculateCATotal(student.id),
            examScore: getExamScore(student.id),
            finalScore: calculateFinalScore(student.id),
            grade: calculateGrade(calculateFinalScore(student.id))
          });
        } catch (error) {
          console.error(`Error fetching report for ${student.name}:`, error);
          // Add student with basic data even if API fails
          reportCardsData.push({
            student,
            reportData: null,
            caTotal: calculateCATotal(student.id),
            examScore: getExamScore(student.id),
            finalScore: calculateFinalScore(student.id),
            grade: calculateGrade(calculateFinalScore(student.id))
          });
        }
      }
      
      // Generate bulk print HTML
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups to print report cards');
        setBulkPrintLoading(false);
        return;
      }
      
      const bulkHtml = generateBulkReportCardsHtml(reportCardsData);
      printWindow.document.write(bulkHtml);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.print();
        toast.success(`✅ ${students.length} report cards ready to print!`);
      };
      
    } catch (error) {
      console.error('Error generating bulk report cards:', error);
      toast.error('Failed to generate report cards. Please try again.');
    } finally {
      setBulkPrintLoading(false);
    }
  };

  // Generate HTML for bulk report cards
  const generateBulkReportCardsHtml = (reportCardsData) => {
    const currentYear = new Date().getFullYear();
    const dateIssued = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    
    // Official Vanuatu A-E Grading Scale (Primary Years 1-6)
    const getGradeInfo = (percentage) => {
      if (percentage >= 85) return { grade: 'A', gpa: 4.0, description: 'Excellent' };
      if (percentage >= 70) return { grade: 'B', gpa: 3.0, description: 'Good' };
      if (percentage >= 50) return { grade: 'C', gpa: 2.0, description: 'Satisfactory' };
      if (percentage >= 30) return { grade: 'D', gpa: 1.0, description: 'Limited' };
      return { grade: 'E', gpa: 0.0, description: 'Low' };
    };
    
    const generateComment = (name, grade, percentage) => {
      const firstName = name.split(' ')[0];
      if (percentage >= 80) return `${firstName} has demonstrated excellent academic performance this term. Keep up the outstanding work!`;
      if (percentage >= 70) return `${firstName} is making very good progress. With continued effort, even better results are achievable.`;
      if (percentage >= 60) return `${firstName} shows satisfactory progress. More consistent study habits will help improve results.`;
      if (percentage >= 50) return `${firstName} is making some progress but needs to focus more on studies and seek help when needed.`;
      return `${firstName} needs significant improvement. I strongly recommend extra support and regular study sessions.`;
    };
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bulk Report Cards - Year ${selectedYear} ${selectedSubject}</title>
        <style>
          @media print {
            .report-card { page-break-after: always; }
            .report-card:last-child { page-break-after: avoid; }
          }
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .report-card { 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 20px;
            background: white;
          }
          .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 15px; margin-bottom: 15px; }
          .header h1 { color: #1e40af; margin: 5px 0; font-size: 20px; }
          .header h2 { color: #374151; margin: 5px 0; font-size: 14px; font-weight: normal; }
          .student-info { 
            display: flex; 
            justify-content: space-between; 
            background: #f3f4f6; 
            padding: 12px; 
            border-radius: 8px; 
            margin-bottom: 15px; 
          }
          .student-info-item { text-align: center; }
          .student-info-item label { font-size: 10px; color: #6b7280; display: block; }
          .student-info-item span { font-weight: bold; font-size: 12px; }
          .grades-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          .grades-table th { background: #1e40af; color: white; padding: 8px; font-size: 10px; text-align: center; }
          .grades-table td { border: 1px solid #e5e7eb; padding: 6px; text-align: center; font-size: 11px; }
          .summary { background: #eff6ff; padding: 15px; border-radius: 8px; margin-bottom: 15px; }
          .summary-grid { display: flex; justify-content: space-around; text-align: center; }
          .summary-stat { }
          .summary-stat-value { font-size: 18px; font-weight: bold; color: #1e40af; }
          .summary-stat-label { font-size: 10px; color: #6b7280; }
          .attendance { background: #f0fdf4; padding: 12px; border-radius: 8px; margin-bottom: 15px; border: 1px solid #86efac; }
          .attendance-title { font-weight: bold; color: #166534; font-size: 11px; margin-bottom: 8px; }
          .attendance-grid { display: flex; justify-content: space-around; text-align: center; }
          .attendance-item { }
          .attendance-value { font-size: 14px; font-weight: bold; }
          .attendance-label { font-size: 9px; color: #6b7280; }
          .comment-section { margin-bottom: 12px; }
          .comment-label { font-weight: bold; font-size: 11px; color: #374151; margin-bottom: 5px; }
          .comment-box { background: #f9fafb; padding: 10px; border-radius: 6px; border-left: 3px solid #1e40af; font-size: 11px; }
          .signature { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #e5e7eb; }
          .signature-line { border-top: 1px solid #000; width: 150px; margin: 30px auto 5px; padding-top: 5px; font-size: 11px; }
          .footer { text-align: center; font-size: 9px; color: #6b7280; margin-top: 15px; }
        </style>
      </head>
      <body>
    `;
    
    reportCardsData.forEach((data, index) => {
      const { student, reportData, caTotal, examScore, finalScore, grade } = data;
      const gradeInfo = getGradeInfo(finalScore);
      const attendance = reportData?.attendance || { present: 0, absent: 0, late: 0, excused: 0, attendance_rate: 0 };
      const stream = reportData?.stream || student.stream || 'General';
      
      html += `
        <div class="report-card">
          <div class="header">
            <h1>${schoolName}</h1>
            <h2>Progress Report - ${selectedTerm} ${currentYear}</h2>
          </div>
          
          <div class="student-info">
            <div class="student-info-item">
              <label>Student Name</label>
              <span>${student.name}</span>
            </div>
            <div class="student-info-item">
              <label>Student ID</label>
              <span>${student.birth_registration || student.student_id || student.id}</span>
            </div>
            <div class="student-info-item">
              <label>Class</label>
              <span>Year ${selectedYear}</span>
            </div>
            <div class="student-info-item">
              <label>Stream</label>
              <span>${stream}</span>
            </div>
            <div class="student-info-item">
              <label>Term</label>
              <span>${selectedTerm}</span>
            </div>
          </div>
          
          <table class="grades-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>CA (40%)</th>
                <th>Exam (60%)</th>
                <th>Total (100%)</th>
                <th>Grade</th>
                <th>Teacher's Comment</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>${selectedSubject}</strong></td>
                <td>${caTotal.toFixed(1)}</td>
                <td>${examScore.toFixed(1)}</td>
                <td><strong>${finalScore.toFixed(1)}</strong></td>
                <td><strong>${gradeInfo.grade}</strong></td>
                <td style="text-align:left;font-size:10px;">${generateComment(student.name, gradeInfo.grade, finalScore)}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="summary">
            <div class="summary-grid">
              <div class="summary-stat">
                <div class="summary-stat-value">${finalScore.toFixed(1)}%</div>
                <div class="summary-stat-label">Overall</div>
              </div>
              <div class="summary-stat">
                <div class="summary-stat-value">${gradeInfo.grade}</div>
                <div class="summary-stat-label">Grade</div>
              </div>
              <div class="summary-stat">
                <div class="summary-stat-value">${gradeInfo.gpa.toFixed(1)}</div>
                <div class="summary-stat-label">GPA</div>
              </div>
              <div class="summary-stat">
                <div class="summary-stat-value">${gradeInfo.description}</div>
                <div class="summary-stat-label">Status</div>
              </div>
            </div>
          </div>
          
          <div class="attendance">
            <div class="attendance-title">ATTENDANCE RECORD</div>
            <div class="attendance-grid">
              <div class="attendance-item">
                <div class="attendance-value" style="color:#166534;">${attendance.present}</div>
                <div class="attendance-label">Present</div>
              </div>
              <div class="attendance-item">
                <div class="attendance-value" style="color:#dc2626;">${attendance.absent}</div>
                <div class="attendance-label">Absent</div>
              </div>
              <div class="attendance-item">
                <div class="attendance-value" style="color:#f59e0b;">${attendance.late}</div>
                <div class="attendance-label">Late</div>
              </div>
              <div class="attendance-item">
                <div class="attendance-value" style="color:#059669;">${attendance.attendance_rate}%</div>
                <div class="attendance-label">Rate</div>
              </div>
            </div>
          </div>
          
          <div class="comment-section">
            <div class="comment-label">Principal's Remarks:</div>
            <div class="comment-box">
              ${finalScore >= 70 
                ? `Well done ${student.name.split(' ')[0]} on achieving good results this term. Continue to work hard and maintain your excellent performance.`
                : finalScore >= 50
                ? `${student.name.split(' ')[0]} shows potential but needs to improve. I encourage more dedication to studies next term.`
                : `${student.name.split(' ')[0]} needs significant improvement. Please work closely with teachers and establish better study habits.`
              }
            </div>
          </div>
          
          <div class="signature">
            <div class="signature-line">Principal</div>
          </div>
          
          <div class="footer">
            <p>This progress report was generated by Penama Education Portal</p>
            <p>Date Issued: ${dateIssued}</p>
          </div>
        </div>
      `;
    });
    
    html += `</body></html>`;
    return html;
  };

  // Check if current selection is Year 13 ICT (special assessment structure)
  const isYear13ICT = () => {
    const isYear13 = selectedYear === "13" || selectedYear === "Year 13" || selectedClass?.name?.includes("13");
    const isICT = selectedSubject?.toLowerCase()?.includes("ict") || 
                  selectedSubject?.toLowerCase()?.includes("computer") ||
                  selectedClass?.subject?.toLowerCase()?.includes("ict") ||
                  selectedClass?.subject?.toLowerCase()?.includes("computer");
    return isYear13 && isICT;
  };

  // Check if current selection is primary school (Years 1-6)
  const isPrimarySchool = () => {
    const year = parseInt(selectedYear) || parseInt(selectedClass?.year);
    return year >= 1 && year <= 6;
  };

  // Check if current selection is secondary school (Years 7-13)
  const isSecondarySchool = () => {
    const year = parseInt(selectedYear) || parseInt(selectedClass?.year);
    return year >= 7 && year <= 13;
  };

  // Vanuatu Secondary Curriculum Strands by Subject (Correct Official Names)
  const secondaryCurriculumStrands = {
    'Computer Studies': {
      strands: ['Personal Computer Systems', 'Application Software', 'Graphics Manipulation', 'Communication and Network Systems'],
      subTopics: ['Hardware & Software', 'Word Processing', 'Spreadsheets', 'Databases', 'DTP', 'Web Development']
    },
    'ICT': {
      strands: ['Personal Computer Systems', 'Application Software', 'Graphics Manipulation', 'Communication and Network Systems'],
      subTopics: ['Hardware & Software', 'Word Processing', 'Spreadsheets', 'Databases', 'DTP', 'Web Development']
    },
    'Chemistry': {
      strands: ['Atomic Structure, Bonding and Related Properties', 'Stoichiometry', 'Rates, Equilibrium and Energetics', 'Acids, Bases and Salts', 'Organic Chemistry'],
      subTopics: ['Atomic Theory', 'Chemical Bonding', 'Mole Concept', 'Reaction Rates', 'Acid-Base Reactions']
    },
    'Mathematics': {
      strands: ['Number & Algebra', 'Measurement & Geometry', 'Statistics & Probability'],
      subTopics: ['Integers', 'Fractions', 'Decimals', 'Equations', 'Area & Perimeter', 'Data Analysis']
    },
    'English': {
      strands: ['Listening & Speaking', 'Reading & Viewing', 'Writing & Shaping'],
      subTopics: ['Comprehension', 'Grammar', 'Vocabulary', 'Essay Writing', 'Oral Presentations']
    },
    'Science': {
      strands: ['Living World', 'Material World', 'Physical World', 'Earth & Space'],
      subTopics: ['Biology', 'Chemistry', 'Physics', 'Environmental Science']
    },
    'Social Science': {
      strands: ['Identity & Culture', 'Place & Environment', 'Time & Change', 'Resources & Economy'],
      subTopics: ['Geography', 'History', 'Economics', 'Civics']
    },
    'French': {
      strands: ['Listening', 'Speaking', 'Reading', 'Writing'],
      subTopics: ['Vocabulary', 'Grammar', 'Comprehension', 'Oral Communication']
    },
    'Agriculture': {
      strands: ['Crop Production', 'Animal Husbandry', 'Agricultural Business', 'Environmental Management'],
      subTopics: ['Planting', 'Harvesting', 'Livestock Care', 'Marketing']
    },
    'Business Studies': {
      strands: ['Business Environment', 'Marketing', 'Finance', 'Management'],
      subTopics: ['Economics', 'Accounting', 'Entrepreneurship', 'Business Law']
    },
    'Religious Education': {
      strands: ['Faith & Beliefs', 'Moral Values', 'Community & Service'],
      subTopics: ['Scripture', 'Ethics', 'Religious Practices', 'Social Responsibility']
    }
  };

  // Vanuatu Primary Curriculum Strands by Subject (Years 1-6)
  // Based on Official Vanuatu National Primary Syllabus
  const primaryCurriculumStrands = {
    // Language and Communication - Official strands from Vanuatu National Curriculum
    'English': {
      strands: ['Listening & Speaking', 'Reading', 'Writing', 'Viewing'],
      subTopics: ['Skills & Strategies', 'Production', 'Context & Texts']
    },
    'Language': {
      strands: ['Listening & Speaking', 'Reading', 'Writing', 'Viewing'],
      subTopics: ['Skills & Strategies', 'Production', 'Context & Texts']
    },
    // Mathematics - Official strands from Vanuatu National Curriculum Years 4-6
    'Mathematics': {
      strands: ['Number', 'Measurement', 'Geometry', 'Patterns', 'Chance & Data'],
      subTopics: ['Whole Numbers', 'Operations', 'Fractions & Decimals', 'Length', 'Area', 'Mass', 'Capacity', 'Time', 'Money', 'Plane Shapes', 'Solids', 'Position', 'Probability', 'Data']
    },
    'Maths': {
      strands: ['Number', 'Measurement', 'Geometry', 'Patterns', 'Chance & Data'],
      subTopics: ['Whole Numbers', 'Operations', 'Fractions & Decimals', 'Length', 'Area', 'Mass', 'Capacity', 'Time', 'Money', 'Plane Shapes', 'Solids', 'Position', 'Probability', 'Data']
    },
    // Science - Official strands from Vanuatu National Curriculum Years 4-6
    'Science': {
      strands: ['Living Things & Environment', 'Interactions of Matter', 'Energy & Everyday Life', 'Our Earth & Space', 'Agriculture'],
      subTopics: ['Living Together', 'Structure & Life Processes', 'Biodiversity', 'Materials', 'Forms & Cycles of Matter', 'Reactions', 'Energy Sources', 'Energy Conservation', 'Forces', 'Solar System', 'Changing Earth', 'Soil Ecosystem', 'Growing Crops', 'Farm Animals']
    },
    // Social Science - Official strands from Vanuatu National Curriculum Years 4-6
    'Social Science': {
      strands: ['Our Cultural Heritage & Identity', 'Civics & Community Relationships', 'Enterprise'],
      subTopics: ['Cultural Values & Beliefs', 'Story of Our Islands', 'Spiritual Development', 'Caring for Environment', 'Living & Working Together', 'Pacific Neighbours', 'Community Governance', 'Enterprise Values & Skills']
    },
    'Living in Community': {
      strands: ['Our Cultural Heritage & Identity', 'Civics & Community Relationships', 'Enterprise'],
      subTopics: ['Cultural Values & Beliefs', 'Story of Our Islands', 'Spiritual Development', 'Caring for Environment', 'Living & Working Together', 'Pacific Neighbours', 'Community Governance', 'Enterprise Values & Skills']
    },
    // French Language
    'French': {
      strands: ['Oral Communication', 'Reading Basics', 'Writing Basics', 'Cultural Awareness'],
      subTopics: ['Greetings', 'Numbers', 'Colors', 'Simple Sentences']
    },
    // Vernacular Language
    'Vernacular': {
      strands: ['Oral Expression', 'Cultural Stories', 'Basic Reading', 'Basic Writing'],
      subTopics: ['Traditional Songs', 'Local Stories', 'Village Knowledge']
    },
    // Physical Education - part of Living in Community "Healthy Living" strand
    'Physical Education': {
      strands: ['Motor Skills', 'Games & Sports', 'Health & Fitness', 'Teamwork'],
      subTopics: ['Running', 'Jumping', 'Ball Games', 'Swimming']
    },
    'PE': {
      strands: ['Motor Skills', 'Games & Sports', 'Health & Fitness', 'Teamwork'],
      subTopics: ['Running', 'Jumping', 'Ball Games', 'Swimming']
    },
    // Arts & Crafts - part of Living in Community "Ways of Living" strand
    'Arts & Crafts': {
      strands: ['Visual Arts', 'Performing Arts', 'Cultural Heritage', 'Creative Expression'],
      subTopics: ['Drawing', 'Painting', 'Music', 'Dance', 'Traditional Crafts']
    },
    'Arts': {
      strands: ['Visual Arts', 'Performing Arts', 'Cultural Heritage', 'Creative Expression'],
      subTopics: ['Drawing', 'Painting', 'Music', 'Dance', 'Traditional Crafts']
    },
    // Agriculture
    'Agriculture': {
      strands: ['Gardening Basics', 'Plant Care', 'Animal Care', 'Food & Nutrition'],
      subTopics: ['Planting Seeds', 'Watering', 'Harvesting', 'Healthy Eating']
    },
    // Religious Education
    'Religious Education': {
      strands: ['Bible Stories', 'Values & Morals', 'Prayer & Worship', 'Community Service'],
      subTopics: ['Creation', 'Kindness', 'Respect', 'Helping Others']
    },
    'RE': {
      strands: ['Bible Stories', 'Values & Morals', 'Prayer & Worship', 'Community Service'],
      subTopics: ['Creation', 'Kindness', 'Respect', 'Helping Others']
    }
  };

  // Get current strand info for selected subject (works for both primary and secondary)
  const getCurrentStrandInfo = () => {
    if (!selectedSubject) return null;
    
    // Check if primary school
    const year = parseInt(selectedYear) || parseInt(selectedClass?.year);
    const isPrimary = year >= 1 && year <= 6;
    
    // Use appropriate curriculum strands
    const curriculumStrands = isPrimary ? primaryCurriculumStrands : secondaryCurriculumStrands;
    
    // Find matching subject (case-insensitive partial match)
    const subjectKey = Object.keys(curriculumStrands).find(
      key => selectedSubject.toLowerCase().includes(key.toLowerCase()) ||
             key.toLowerCase().includes(selectedSubject.toLowerCase())
    );
    return subjectKey ? curriculumStrands[subjectKey] : null;
  };

  // Helper function to parse database grades and group by strand/type
  // Returns: { caAssessments: [{title, strand, type, maxMarks}...], examAssessment: {...}, studentGrades: {studentId: {assessmentTitle: {marks, percentage}}} }
  const getProcessedGrades = () => {
    const caAssessments = [];
    const examAssessments = [];
    const studentGrades = {};
    
    // Process grades from database
    Object.entries(grades).forEach(([studentId, studentGradeList]) => {
      if (!studentGrades[studentId]) {
        studentGrades[studentId] = {};
      }
      
      studentGradeList.forEach(grade => {
        const title = grade.assessment_title || '';
        studentGrades[studentId][title] = {
          marks: grade.raw_marks,
          maxMarks: grade.max_marks,
          percentage: grade.percentage
        };
        
        // Parse title to determine if CA or Exam
        // Format: "S1-Quiz - Term 1" (CA) or "Exam - Term 1" (Exam)
        const isExam = title.toLowerCase().includes('exam');
        
        if (isExam) {
          if (!examAssessments.find(a => a.title === title)) {
            examAssessments.push({
              title,
              type: 'exam',
              maxMarks: grade.max_marks
            });
          }
        } else {
          // Parse strand and type from title (e.g., "S2-Test - Term 1")
          const match = title.match(/^(S\d+)-(\w+)\s*-\s*(.+)$/i);
          if (match && !caAssessments.find(a => a.title === title)) {
            caAssessments.push({
              title,
              strand: match[1],
              type: match[2].toLowerCase(),
              term: match[3],
              maxMarks: grade.max_marks
            });
          } else if (!match && !caAssessments.find(a => a.title === title)) {
            // Old format without strand (e.g., "Quiz - Term 1")
            caAssessments.push({
              title,
              strand: null,
              type: title.split(' - ')[0].toLowerCase(),
              term: title.split(' - ')[1] || 'Term 1',
              maxMarks: grade.max_marks
            });
          }
        }
      });
    });
    
    // Sort CA assessments by strand then type
    caAssessments.sort((a, b) => {
      if (a.strand && b.strand) {
        return a.strand.localeCompare(b.strand) || a.type.localeCompare(b.type);
      }
      return a.title.localeCompare(b.title);
    });
    
    return { caAssessments, examAssessments, studentGrades };
  };

  // Calculate CA total for a student (all non-exam assessments from DB)
  const calculateDBCATotal = (studentId, processedGrades) => {
    const studentGrade = processedGrades.studentGrades[studentId] || {};
    let totalMarks = 0;
    let totalMaxMarks = 0;
    
    processedGrades.caAssessments.forEach(assessment => {
      const grade = studentGrade[assessment.title];
      if (grade) {
        totalMarks += grade.marks;
        totalMaxMarks += grade.maxMarks;
      }
    });
    
    // Scale to 40% (CA weight)
    const caPercentage = totalMaxMarks > 0 ? (totalMarks / totalMaxMarks) * 40 : 0;
    return { totalMarks, totalMaxMarks, scaledScore: Math.round(caPercentage * 10) / 10 };
  };

  // Get exam score for a student from DB (scaled to 60%)
  const getDBExamScore = (studentId, processedGrades) => {
    const studentGrade = processedGrades.studentGrades[studentId] || {};
    
    for (const exam of processedGrades.examAssessments) {
      const grade = studentGrade[exam.title];
      if (grade) {
        // Scale to 60% (Exam weight)
        const scaledScore = (grade.percentage / 100) * 60;
        return { marks: grade.marks, maxMarks: grade.maxMarks, scaledScore: Math.round(scaledScore * 10) / 10 };
      }
    }
    return { marks: 0, maxMarks: 0, scaledScore: 0 };
  };

  // Max marks for each assessment type (Year 13 ICT only)
  const assessmentMaxMarks = {
    quiz: 15,
    homework: 10,
    practical: 25,
    project: 30,
    test: 20,
    exercise: 20,
    assignment: 20,
    presentation: 20,
    observation: 15
  };
  const totalCAMaxMarks = 175; // Sum of all assessment max marks (15+10+25+30+20+20+20+20+15)

  // Secondary school assessment structure (Years 7-13) - simplified
  const secondaryAssessmentMaxMarks = {
    quiz: 10,       // Class quizzes / Unit tests
    homework: 10,   // Homework / Assignments
    project: 10,    // Projects / Practicals
    classwork: 10,  // Classwork / Exercises
  };
  const secondaryCAMaxMarks = 40; // Sum = 40% (CA)
  const secondaryExamMaxMarks = 60; // Exam = 60%

  // Primary school assessment structure (Years 4-6) - Syllabus-based
  // Based on official Vanuatu syllabus for English, Maths, Science
  const primaryAssessmentMaxMarks = {
    // Legacy strand-based marks
    'strand1-ca': 10,
    'strand1-test': 10,
    'strand2-ca': 10,
    'strand2-test': 10,
    
    // English Assessment Types (Listening & Speaking, Reading, Writing, Viewing)
    'oral-presentation': 10,
    'debate': 10,
    'role-play': 10,
    'story-telling': 10,
    'dialogue': 10,
    'oral-reading': 10,
    'comprehension': 10,
    'cloze-activity': 10,
    'book-review': 10,
    'spelling': 10,
    'grammar': 10,
    'handwriting': 10,
    'writing-draft': 10,
    'visual-text': 10,
    
    // Mathematics Assessment Types (Number, Measurement, Geometry, Patterns, Chance & Data)
    'mental-calculation': 10,
    'worksheet': 10,
    'investigation': 10,
    'practical-activity': 10,
    'measurement': 10,
    'geometric-drawing': 10,
    'operations': 10,
    'number-facts': 10,
    'tables-test': 10,
    'graph-chart': 10,
    'pattern-work': 10,
    
    // Science Assessment Types (Living Things, Matter, Energy, Earth & Space)
    'observation': 10,
    'experiment': 10,
    'diagram': 10,
    'model': 10,
    'science-sketch': 10,
    'food-chain': 10,
    'predictions': 10,
    'safety-skills': 10,
    
    // General assessment types
    'quiz': 10,
    'test': 10,
    'project': 10,
    'homework': 10,
    'presentation': 10,
    // 'observation' already defined above in Science section
  };
  const primaryCAMaxMarks = 40; // Total CA = 40 marks
  const primaryExamMaxMarks = 60; // Exam is 60% of total

  // Get assessment mark for a student
  const getAssessmentMark = (studentId, type) => {
    return assessmentMarks[`${studentId}-${type}`] ?? null;
  };

  // Update assessment mark - now auto-saves to database
  const updateAssessmentMark = async (studentId, type, value) => {
    const maxMarks = assessmentMaxMarks[type] || primaryAssessmentMaxMarks[type] || 10;
    const numValue = Math.min(Math.max(0, parseInt(value) || 0), maxMarks);
    
    // Update local state immediately for responsive UI
    setAssessmentMarks(prev => ({
      ...prev,
      [`${studentId}-${type}`]: numValue
    }));
    
    // Only save if value is not empty
    if (value === '' || value === null || value === undefined) return;
    
    // Extract strand from type (e.g., "strand1-quiz" -> "strand1", "quiz")
    const typeParts = type.split('-');
    const strand = typeParts.length > 1 ? typeParts[0] : 'strand1';
    const assessmentType = typeParts.length > 1 ? typeParts[1] : type;
    
    try {
      const classId = selectedClass?.id;
      if (!classId) {
        console.error('No class selected for saving grade');
        return;
      }
      
      const result = await apiService.quickGradeEntry(
        classId,
        assessmentType,
        selectedTerm,
        maxMarks,
        [{ student_id: studentId, raw_marks: numValue }],
        strand
      );
      
      // Refresh grades from API to sync the Strand View table
      if (classId) {
        const classGradesMap = await apiService.getClassGrades(classId);
        setGrades(classGradesMap || {});
      }
    } catch (error) {
      console.error('Failed to save grade:', error);
    }
  };

  // Get exam mark for a student
  const getExamMark = (studentId) => {
    return examMarks[studentId] ?? null;
  };

  // Update exam mark - now auto-saves to database
  const updateExamMark = async (studentId, value) => {
    const maxMark = examTotalMarks || 100;
    const numValue = Math.min(Math.max(0, parseInt(value) || 0), maxMark);
    
    // Update local state immediately
    setExamMarks(prev => ({
      ...prev,
      [studentId]: numValue
    }));
    
    // Only save if value is not empty
    if (value === '' || value === null || value === undefined) return;
    
    // Auto-save to database
    try {
      const classId = selectedClass?.id;
      if (!classId) {
        console.error('No class selected for saving exam grade');
        return;
      }
      
      await apiService.quickGradeEntry(
        classId,
        'exam',
        selectedTerm,
        maxMark,
        [{ student_id: studentId, raw_marks: numValue }],
        null
      );
      
      // Refresh grades from API to sync the Strand View table
      if (classId) {
        const classGradesMap = await apiService.getClassGrades(classId);
        setGrades(classGradesMap || {});
      }
    } catch (error) {
      console.error('Failed to save exam grade:', error);
    }
  };

  // Update exam total marks
  const updateExamTotalMarks = (value) => {
    const numValue = Math.max(1, parseInt(value) || 100);
    setExamTotalMarks(numValue);
  };

  // ============================================
  // AUTOMATIC GRADING FUNCTIONS
  // ============================================
  
  // Traffic Light Status based on total score
  // Green: On Track (60%+), Yellow: Needs Support (40-59%), Red: Urgent Action (<40%)
  const getTrafficLightStatus = (totalScore) => {
    if (totalScore >= 60) return 'green';   // On Track
    if (totalScore >= 40) return 'yellow';  // Needs Support
    return 'red';                            // Urgent Action
  };
  
  // Grade scale configuration (can be customized per assessment)
  const defaultGradeScale = {
    // Official Vanuatu A-E Grading Scale (Primary Years 1-6)
    'A': { min: 85, max: 100, color: 'bg-emerald-500', textColor: 'text-emerald-700', description: 'Excellent' },
    'B': { min: 70, max: 84, color: 'bg-blue-500', textColor: 'text-blue-700', description: 'Good' },
    'C': { min: 50, max: 69, color: 'bg-yellow-500', textColor: 'text-yellow-700', description: 'Satisfactory' },
    'D': { min: 30, max: 49, color: 'bg-orange-500', textColor: 'text-orange-700', description: 'Limited' },
    'E': { min: 1, max: 29, color: 'bg-red-500', textColor: 'text-red-700', description: 'Low' }
  };

  // Calculate grade from percentage
  const calculateGrade = (percentage, customScale = null) => {
    const scale = customScale || defaultGradeScale;
    for (const [grade, range] of Object.entries(scale)) {
      if (percentage >= range.min && percentage <= range.max) {
        return { grade, ...range };
      }
    }
    return { grade: 'E', ...scale['E'] };
  };

  // Calculate percentage from marks
  const calculatePercentage = (marks, totalMarks) => {
    if (!marks || !totalMarks || totalMarks === 0) return 0;
    return Math.round((marks / totalMarks) * 100);
  };

  // Get auto-calculated grade for a student's assessment
  const getAutoGrade = (studentId, assessmentId, marks, totalMarks) => {
    if (marks === null || marks === undefined) return null;
    const percentage = calculatePercentage(marks, totalMarks);
    return calculateGrade(percentage);
  };

  // Get grade badge component
  const GradeBadge = ({ marks, totalMarks, showPercentage = true }) => {
    if (marks === null || marks === undefined) return null;
    const percentage = calculatePercentage(marks, totalMarks);
    const gradeInfo = calculateGrade(percentage);
    
    return (
      <div className="flex items-center gap-1">
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold text-white ${gradeInfo.color}`}>
          {gradeInfo.grade}
        </span>
        {showPercentage && (
          <span className={`text-xs ${gradeInfo.textColor}`}>
            {percentage}%
          </span>
        )}
      </div>
    );
  };

  // Calculate total marks and grade for a student across all assessments
  const calculateStudentTotalGrade = (studentId) => {
    let totalEarned = 0;
    let totalPossible = 0;

    // Add CA marks (if secondary school structure)
    if (isSecondarySchool()) {
      const caTypes = ['quiz', 'homework', 'project', 'classwork'];
      caTypes.forEach(type => {
        const mark = getAssessmentMark(studentId, type);
        if (mark !== null) {
          totalEarned += mark;
          totalPossible += secondaryAssessmentMaxMarks[type];
        }
      });
    }

    // Add exam marks
    const examMark = getExamMark(studentId);
    if (examMark !== null) {
      // Scale exam marks to 60%
      const examPercentage = (examMark / examTotalMarks) * 60;
      totalEarned += examPercentage;
      totalPossible += 60;
    }

    // If we have CA, scale it to 40%
    if (totalPossible > 60) {
      const caEarned = totalEarned - (examMark ? (examMark / examTotalMarks) * 60 : 0);
      const caMax = totalPossible - 60;
      const scaledCA = (caEarned / caMax) * 40;
      totalEarned = scaledCA + (examMark ? (examMark / examTotalMarks) * 60 : 0);
      totalPossible = 100;
    }

    if (totalPossible === 0) return null;
    
    const percentage = Math.round((totalEarned / totalPossible) * 100);
    return {
      earned: Math.round(totalEarned * 10) / 10,
      possible: totalPossible,
      percentage,
      ...calculateGrade(percentage)
    };
  };

  // Get selected class from localStorage
  useEffect(() => {
    const storedClass = localStorage.getItem('selectedClass');
    if (storedClass) {
      const classData = JSON.parse(storedClass);
      setSelectedClass(classData);
      setSelectedYear(classData.year.toString());
      setSelectedSubject(classData.subject);
    }
  }, []);

  // Listen for class changes from header dropdown
  useEffect(() => {
    const handleStorageChange = () => {
      const storedClass = localStorage.getItem('selectedClass');
      if (storedClass) {
        const classData = JSON.parse(storedClass);
        setSelectedClass(classData);
        setSelectedYear(classData.year.toString());
        setSelectedSubject(classData.subject);
      }
    };

    // Listen for same-tab class selection changes (custom event)
    const handleClassSelectionChanged = (event) => {
      const classData = event.detail;
      if (classData) {
        setSelectedClass(classData);
        setSelectedYear(classData.year.toString());
        setSelectedSubject(classData.subject);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('classSelectionChanged', handleClassSelectionChanged);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('classSelectionChanged', handleClassSelectionChanged);
    };
  }, []);

  // Check for exam from AI Generator and sync max marks
  useEffect(() => {
    // Check location state for exam data
    if (location.state?.examFromGenerator) {
      const examData = location.state.examFromGenerator;
      if (examData.totalMarks) {
        setExamTotalMarks(examData.totalMarks);
        setBatchEntryOpen(true);
        setBatchEntryType('exam');
        toast.success(`Exam max marks set to ${examData.totalMarks}`, {
          description: `${examData.title} - Ready for batch entry`
        });
      }
    }
    
    // Also check localStorage for pending exam
    const pendingExam = localStorage.getItem('pendingExamForGradeBook');
    if (pendingExam) {
      try {
        const examData = JSON.parse(pendingExam);
        if (examData.totalMarks) {
          setExamTotalMarks(examData.totalMarks);
          setBatchEntryOpen(true);
          setBatchEntryType('exam');
          toast.success(`Exam max marks set to ${examData.totalMarks}`, {
            description: `${examData.title} - Ready for batch entry`
          });
        }
        // Clear the pending exam after loading
        localStorage.removeItem('pendingExamForGradeBook');
      } catch (e) {
        console.error('Error parsing pending exam:', e);
      }
    }
  }, [location.state]);

  // Fetch teacher's subjects and years on mount  
  useEffect(() => {
    const fetchTeacherData = async (retryCount = 0) => {
      try {
        // For principals, use skip_counts for faster initial load
        const isPrincipal = currentUser?.role === 'principal';
        const teacherClasses = await apiService.getTeacherClasses(isPrincipal);
        
        // Store all classes for filtering subjects by year
        setAllClasses(teacherClasses);
        
        // Extract unique years and sort them
        const uniqueYears = [...new Set(teacherClasses.map(c => c.year.toString()))].sort((a, b) => parseInt(a) - parseInt(b));
        setYears(uniqueYears);
        
        // Set default year to first available year
        if (uniqueYears.length > 0) {
          const defaultYear = uniqueYears[0];
          setSelectedYear(defaultYear);
          
          // Filter subjects for the default year
          const subjectsForYear = [...new Set(
            teacherClasses
              .filter(c => c.year.toString() === defaultYear)
              .map(c => c.subject)
          )].sort();
          setSubjects(subjectsForYear);
          
          // Set default subject to first available subject for this year
          if (subjectsForYear.length > 0 && !selectedSubject) {
            setSelectedSubject(subjectsForYear[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching teacher data:', error);
        if (retryCount < 2) {
          // Retry after delay (handles Neon cold start)
          setTimeout(() => fetchTeacherData(retryCount + 1), 3000);
        } else {
          toast.error('Failed to load class data. Please refresh the page.');
        }
      }
    };

    fetchTeacherData();
  }, [currentUser?.role]);
  
  // Update subjects list when year changes
  useEffect(() => {
    if (allClasses.length > 0 && selectedYear) {
      // Filter subjects for the selected year
      const subjectsForYear = [...new Set(
        allClasses
          .filter(c => c.year.toString() === selectedYear)
          .map(c => c.subject)
      )].sort();
      setSubjects(subjectsForYear);
      
      // If current subject is not available for this year, reset to first available
      if (subjectsForYear.length > 0 && !subjectsForYear.includes(selectedSubject)) {
        setSelectedSubject(subjectsForYear[0]);
      }
    }
  }, [selectedYear, allClasses]);

  // Fetch students and grades when class selection or term changes
  useEffect(() => {
    if (!selectedSubject || !selectedYear) return; // Don't fetch if no subject or year selected yet
    
    // Show loading state
    setLoading(true);
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get the actual class ID - filter by year and subject for faster query
        const teacherClasses = await apiService.getTeacherClasses(false, selectedYear, selectedSubject);
        const matchingClasses = teacherClasses.filter(c => 
          c.year.toString() === selectedYear && 
          c.subject === selectedSubject
        );
        
        // For principals with multiple classes for same year/subject, show teacher picker
        if (matchingClasses.length > 1 && currentUser?.role === 'principal') {
          setAvailableTeachers(matchingClasses);
          let matchedClass;
          if (selectedTeacherId) {
            matchedClass = matchingClasses.find(c => c.teacher_id === selectedTeacherId);
          }
          if (!matchedClass) {
            // Check which class has assessments by trying each
            for (const cls of matchingClasses) {
              try {
                const assessments = await apiService.listAssessments(cls.id);
                if (assessments && assessments.length > 0) {
                  matchedClass = cls;
                  break;
                }
              } catch (e) { /* skip */ }
            }
          }
          if (!matchedClass) matchedClass = matchingClasses[0];
          setSelectedTeacherId(matchedClass.teacher_id);
          var finalClass = matchedClass;
        } else {
          setAvailableTeachers([]);
          var finalClass = matchingClasses[0];
        }
        let matchedClass = finalClass;
        
        if (matchedClass) {
          // Store the matched class for use in grade saving functions
          setSelectedClass(matchedClass);
          
          // Fetch students from the class enrollment endpoint
          try {
            const response = await fetch(
              `/api/classes/${matchedClass.id}/students`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
              }
            );
            if (response.ok) {
              const studentsData = await response.json();
              // Format students with id if not present
              const formattedStudents = studentsData.map((s, idx) => ({
                ...s,
                id: s.id || s.student_id || `student-${idx}`,
                name: s.name || `Student ${idx + 1}`
              }));
              setStudents(formattedStudents);
            } else {
              // Fallback to empty students if API fails
              setStudents([]);
            }
          } catch (e) {
            console.error('Error fetching students:', e);
            setStudents([]);
          }
          
          // Fetch assessments for the class
          try {
            const allAssessmentsData = await apiService.listAssessments(matchedClass.id);
            
            // Filter assessments by selected term
            let filteredAssessments = allAssessmentsData || [];
            if (selectedTerm !== "All Terms" && filteredAssessments.length > 0) {
              filteredAssessments = filteredAssessments.filter(a => a.term === selectedTerm);
            }
            setAssessments(filteredAssessments);
          } catch (e) {
            console.error('Error fetching assessments:', e);
            setAssessments([]);
          }

          // Fetch grades for the class
          try {
            const classGradesMap = await apiService.getClassGrades(matchedClass.id);
            setGrades(classGradesMap || {});
          } catch (e) {
            console.error('Error fetching grades:', e);
            setGrades({});
          }
        } else {
          // No matching class found, reset data
          setSelectedClass(null);
          setStudents([]);
          setAssessments([]);
          setGrades({});
        }
      } catch (error) {
        console.error('Error fetching grade book data:', error);
        // Don't show error toast for empty data - this is expected for new classes
        setStudents([]);
        setAssessments([]);
        setGrades({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, selectedSubject, selectedTerm, selectedTeacherId]);

  // Calculate CA Total (40% of final grade) - from fixed assessment columns
  const calculateCATotal = (studentId) => {
    // Check if primary school (Years 1-6)
    const year = parseInt(selectedYear) || parseInt(selectedClass?.year);
    const isPrimary = year >= 1 && year <= 6;
    
    // First try to get grades from database
    const studentDbGrades = grades[studentId] || [];
    
    if (studentDbGrades.length > 0) {
      // Use database grades for calculation
      let totalMarks = 0;
      let totalMaxMarks = 0;
      
      studentDbGrades.forEach(grade => {
        const title = (grade.assessment_title || '').toLowerCase();
        // Only include CA assessments (exclude any exam)
        if (!title.includes('exam')) {
          totalMarks += grade.raw_marks || 0;
          totalMaxMarks += grade.max_marks || 0;
        }
      });
      
      // Scale to 40% (CA weight)
      if (totalMaxMarks > 0) {
        const caPercentage = (totalMarks / totalMaxMarks) * 100;
        const caTotal = (caPercentage / 100) * 40;
        return Math.round(caTotal * 10) / 10;
      }
    }
    
    // Fallback to local state if no database grades
    if (isPrimary) {
      // Primary school calculation - Strand-based (S1 Quiz, S1 Homework, S2 Project, S2 Classwork)
      const s1QuizMark = getAssessmentMark(studentId, 'strand1-quiz') || 0;
      const s1HomeworkMark = getAssessmentMark(studentId, 'strand1-homework') || 0;
      const s2ProjectMark = getAssessmentMark(studentId, 'strand2-project') || 0;
      const s2ClassworkMark = getAssessmentMark(studentId, 'strand2-classwork') || 0;
      
      // Sum all primary assessment marks (max 40: 10+10+15+5)
      const totalMarks = s1QuizMark + s1HomeworkMark + s2ProjectMark + s2ClassworkMark;
      return totalMarks; // Already out of 40
    }
    
    // Secondary school calculation (Year 13 ICT style)
    // Get marks from each fixed assessment type
    const quizMark = getAssessmentMark(studentId, 'quiz') || 0;
    const homeworkMark = getAssessmentMark(studentId, 'homework') || 0;
    const practicalMark = getAssessmentMark(studentId, 'practical') || 0;
    const projectMark = getAssessmentMark(studentId, 'project') || 0;
    const testMark = getAssessmentMark(studentId, 'test') || 0;
    const exerciseMark = getAssessmentMark(studentId, 'exercise') || 0;
    const assignmentMark = getAssessmentMark(studentId, 'assignment') || 0;
    
    // Sum all assessment marks
    const totalMarks = quizMark + homeworkMark + practicalMark + projectMark + testMark + exerciseMark + assignmentMark;
    
    // Calculate percentage and convert to 40% weighting
    const percentage = (totalMarks / totalCAMaxMarks) * 100;
    const caTotal = (percentage / 100) * 40; // 40% weighting
    
    return caTotal;
  };

  // Get exam score (60% of final grade) - from database or raw exam mark input
  const getExamScore = (studentId) => {
    // First try database grades
    const studentDbGrades = grades[studentId] || [];
    
    let examTotalMarksObtained = 0;
    let examTotalMaxMarks = 0;
    
    for (const grade of studentDbGrades) {
      const title = (grade.assessment_title || '').toLowerCase();
      // Match exam titles: "exam", "end of term", "end of term1", etc.
      if (title.startsWith('exam') || title.includes('exam')) {
        examTotalMarksObtained += grade.raw_marks || 0;
        examTotalMaxMarks += grade.max_marks || 0;
      }
    }
    
    if (examTotalMaxMarks > 0) {
      const examPercentage = (examTotalMarksObtained / examTotalMaxMarks) * 100;
      const examScore = (examPercentage / 100) * 60;
      return Math.round(examScore * 10) / 10;
    }
    
    // Fallback to local state
    const examMark = getExamMark(studentId);
    if (examMark === null) return 0;
    
    // Convert raw mark (out of examTotalMarks) to 60% weighting
    const maxMark = examTotalMarks || 100;
    const examScore = (examMark / maxMark) * 60;
    return examScore;
  };

  // Calculate Final Score = CA (40%) + Exam (60%)
  const calculateFinalScore = (caTotal, examScore) => {
    return caTotal + examScore;
  };

  // Calculate GPA (4.0 scale) based on Final Score
  const calculateGPA = (finalScore) => {
    // Aligned with Official Vanuatu A-E Grading Scale
    if (finalScore >= 85) return 4.0;
    if (finalScore >= 70) return 3.0;
    if (finalScore >= 50) return 2.0;
    if (finalScore >= 30) return 1.0;
    if (finalScore > 0) return 0.5;
    return 0.0;
  };

  // Get letter grade from final score
  const getLetterGrade = (finalScore) => {
    // Official Vanuatu A-E Grading Scale
    if (finalScore >= 85) return 'A';
    if (finalScore >= 70) return 'B';
    if (finalScore >= 50) return 'C';
    if (finalScore >= 30) return 'D';
    return 'E';
  };

  // Calculate student statistics with ranking
  const getStudentStatsWithRanking = () => {
    const studentStats = students.map(student => {
      const caTotal = calculateCATotal(student.id);
      const examScore = getExamScore(student.id); // Now using real exam scores
      const finalScore = calculateFinalScore(caTotal, examScore);
      const gpa = calculateGPA(finalScore);
      const grade = getLetterGrade(finalScore);

      return {
        studentId: student.id,
        studentName: student.name,
        caTotal,
        examScore,
        finalScore,
        gpa,
        grade
      };
    });

    // Sort by final score descending and assign ranks
    studentStats.sort((a, b) => b.finalScore - a.finalScore);
    studentStats.forEach((stat, index) => {
      stat.rank = index + 1;
    });

    return studentStats;
  };

  const studentStatsWithRanking = getStudentStatsWithRanking();

  // Get stats for specific student
  const getStudentStats = (studentId) => {
    const stat = studentStatsWithRanking.find(s => s.studentId === studentId);
    return stat || { caTotal: 0, examScore: 0, finalScore: 0, gpa: 0.0, grade: 'N/A', rank: '-' };
  };

  // Calculate class average GPA
  const calculateClassGPA = () => {
    if (studentStatsWithRanking.length === 0) return '0.00';
    const totalGPA = studentStatsWithRanking.reduce((sum, s) => sum + s.gpa, 0);
    return (totalGPA / studentStatsWithRanking.length).toFixed(2);
  };

  // Traffic Light Analysis
  const getTrafficLight = (finalScore) => {
    if (finalScore >= 70) {
      return {
        color: 'bg-green-500',
        textColor: 'text-green-700',
        bgLight: 'bg-green-50',
        icon: '🟢',
        label: 'Good',
        description: 'On Track'
      };
    } else if (finalScore >= 50) {
      return {
        color: 'bg-yellow-500',
        textColor: 'text-yellow-700',
        bgLight: 'bg-yellow-50',
        icon: '🟡',
        label: 'At Risk',
        description: 'Needs Support'
      };
    } else {
      return {
        color: 'bg-red-500',
        textColor: 'text-red-700',
        bgLight: 'bg-red-50',
        icon: '🔴',
        label: 'Failing',
        description: 'Urgent Action'
      };
    }
  };

  // Get traffic light statistics for the entire class
  const getTrafficLightStats = () => {
    const stats = { green: 0, yellow: 0, red: 0, total: students.length };
    
    studentStatsWithRanking.forEach(stat => {
      const tl = getTrafficLight(stat.finalScore);
      if (tl.label === 'Good') stats.green++;
      else if (tl.label === 'At Risk') stats.yellow++;
      else stats.red++;
    });
    
    return stats;
  };

  const trafficLightStats = getTrafficLightStats();

  // Handle grade editing
  const handleCellClick = (studentId, assessmentId, currentValue) => {
    setEditingCell({ studentId, assessmentId });
    setTempValue(currentValue || '');
  };

  const handleSaveGrade = async () => {
    if (!editingCell) return;

    const { studentId, assessmentId } = editingCell;
    const assessment = assessments.find(a => a.id === assessmentId);
    
    if (!assessment) return;

    // Handle empty value - delete the grade
    if (tempValue === '' || tempValue === null || tempValue === undefined) {
      try {
        // Find the grade to delete
        const studentGrades = grades[studentId] || [];
        const existingGrade = studentGrades.find(g => g.assessment_id === assessmentId);
        if (existingGrade && existingGrade.id) {
          await apiService.deleteGrade(existingGrade.id);
        }
        // Remove from local state
        setGrades(prev => ({
          ...prev,
          [studentId]: (prev[studentId] || []).filter(g => g.assessment_id !== assessmentId)
        }));
        toast.success('Grade cleared');
        setEditingCell(null);
        setTempValue('');
        return;
      } catch (error) {
        console.error('Error clearing grade:', error);
        toast.error('Failed to clear grade');
        return;
      }
    }

    const rawMarks = parseFloat(tempValue);
    if (isNaN(rawMarks) || rawMarks < 0 || rawMarks > assessment.total_marks) {
      toast.error(`Invalid mark. Must be between 0 and ${assessment.total_marks}`);
      return;
    }

    try {
      await apiService.createGrade({
        student_id: studentId,
        assessment_id: assessmentId,
        raw_marks: rawMarks,
        max_marks: assessment.total_marks
      });

      // Update local state
      setGrades(prev => ({
        ...prev,
        [studentId]: [
          ...(prev[studentId] || []).filter(g => g.assessment_id !== assessmentId),
          {
            assessment_id: assessmentId,
            raw_marks: rawMarks,
            max_marks: assessment.total_marks,
            percentage: (rawMarks / assessment.total_marks) * 100
          }
        ]
      }));

      toast.success('Grade saved successfully');
      setEditingCell(null);
      setTempValue('');
    } catch (error) {
      console.error('Error saving grade:', error);
      toast.error('Failed to save grade');
    }
  };

  const getGradeForCell = (studentId, assessmentId) => {
    const studentGrades = grades[studentId] || [];
    const grade = studentGrades.find(g => g.assessment_id === assessmentId);
    return grade ? grade.raw_marks : null;
  };

  const handleOpenReport = async (student) => {
    // For primary schools, use the PrimaryReportCard
    if (isPrimarySchool()) {
      try {
        // Fetch attendance data for primary student
        let attendanceData = { absent: 0, present: 0, late: 0, total_days: 0 };
        try {
          attendanceData = await apiService.getStudentAttendance(student.id) || attendanceData;
        } catch (error) {
          console.warn('Could not fetch attendance data:', error);
        }
        
        // Fetch report card data from database (includes all subjects with grades)
        let reportData = null;
        try {
          reportData = await apiService.getStudentReportCard(
            student.id,
            parseInt(selectedYear),
            selectedTerm
          );
        } catch (error) {
          console.warn('Could not fetch report card data:', error);
        }
        
        setSelectedStudentForReport({
          ...student,
          attendanceData,
          reportData // Include the full report card data from API
        });
      } catch (error) {
        console.warn('Error in handleOpenReport:', error);
        setSelectedStudentForReport({
          ...student,
          attendanceData: { absent: 0, present: 0, late: 0, total_days: 0 },
          reportData: null
        });
      }
      setReportCardOpen(true);
      return;
    }
    
    // For secondary schools, use the new SecondaryReportCard component
    try {
      // Get current student stats from gradebook
      const stats = getStudentStats(student.id);
      
      // Build grades data for the current subject from local state
      const currentSubjectGrade = {
        name: selectedSubject,
        ca: Math.round(stats.caTotal),
        exam: Math.round(stats.examScore),
        total: Math.round(stats.finalScore),
        classAvg: calculateClassAverage(),
        ranking: stats.rank,
        absences: 0,
        grade: stats.grade,
        gpa: stats.gpa
      };
      
      // Try to fetch consolidated report card with all subjects from all teachers
      let reportData;
      try {
        reportData = await apiService.getStudentReportCard(
          student.id, 
          parseInt(selectedYear), 
          selectedTerm
        );
        
        // Transform API data to SecondaryReportCard format
        if (reportData?.subjects) {
          const transformedGrades = reportData.subjects.map(subject => ({
            name: subject.subject,
            ca: Math.round(subject.ca_total || (subject.percentage || 0) * 0.4),
            exam: Math.round(subject.exam_score || (subject.percentage || 0) * 0.6),
            total: Math.round(subject.percentage || 0),
            classAvg: subject.class_average || 0,
            ranking: subject.ranking || 0,
            absences: subject.absences || 0,
            grade: '',
            gpa: 0
          }));
          
          setSelectedReportStudent({
            ...student,
            gradesForReport: transformedGrades,
            reportData: reportData,
            attendanceData: reportData.attendance ? {
              present: reportData.attendance.present || 0,
              absent: reportData.attendance.absent || 0,
              late: reportData.attendance.late || 0,
              total: reportData.attendance.total_days || 0,
              percentage: reportData.attendance.attendance_rate || 0
            } : {
              present: 0,
              absent: 0,
              late: 0,
              total: 0,
              percentage: 0
            }
          });
        } else {
          // Fallback with current subject only - try to fetch attendance separately
          let attendanceData = { present: 0, absent: 0, late: 0, total: 0, percentage: 0 };
          try {
            const attendanceSummary = await apiService.getStudentAttendance(student.id);
            if (attendanceSummary) {
              attendanceData = {
                present: attendanceSummary.present || 0,
                absent: attendanceSummary.absent || 0,
                late: attendanceSummary.late || 0,
                total: attendanceSummary.total_days || 0,
                percentage: attendanceSummary.percentage || 0
              };
            }
          } catch (e) {
            console.warn('Could not fetch attendance data:', e);
          }
          
          setSelectedReportStudent({
            ...student,
            gradesForReport: [currentSubjectGrade],
            reportData: null,
            attendanceData: attendanceData
          });
        }
      } catch (apiError) {
        console.warn('Multi-subject API not ready, using current subject:', apiError);
        // Fallback: Use current subject data only - try to fetch attendance separately
        let attendanceData = { present: 0, absent: 0, late: 0, total: 0, percentage: 0 };
        try {
          const attendanceSummary = await apiService.getStudentAttendance(student.id);
          if (attendanceSummary) {
            attendanceData = {
              present: attendanceSummary.present || 0,
              absent: attendanceSummary.absent || 0,
              late: attendanceSummary.late || 0,
              total: attendanceSummary.total_days || 0,
              percentage: attendanceSummary.percentage || 0
            };
          }
        } catch (e) {
          console.warn('Could not fetch attendance data:', e);
        }
        
        setSelectedReportStudent({
          ...student,
          gradesForReport: [currentSubjectGrade],
          reportData: null,
          attendanceData: attendanceData
        });
      }
      
      setReportCardOpen(true);
    } catch (error) {
      console.error('Error opening report card:', error);
      toast.error('Failed to load report card');
    }
  };
  
  // Calculate class average score
  const calculateClassAverage = () => {
    if (students.length === 0) return 0;
    const totalScore = students.reduce((sum, student) => {
      const stats = getStudentStats(student.id);
      return sum + stats.finalScore;
    }, 0);
    return Math.round(totalScore / students.length);
  };


  // Assessment names are now directly from blueprint - no mapping needed
  // Database assessments already have the correct blueprint names

  // Handle creating assessment from curriculum
  const handleCreateCurriculumAssessment = async () => {
    if (!assessmentFromCurriculum || !selectedClass) {
      toast.error('Please select a class first');
      return;
    }

    try {
      // Format term properly
      let formattedTerm = assessmentFromCurriculum.term;
      if (formattedTerm && typeof formattedTerm === 'string') {
        formattedTerm = formattedTerm.charAt(0).toUpperCase() + formattedTerm.slice(1).replace(/(\d)/, ' $1'); // "term1" -> "Term 1"
      }

      // Create a new assessment based on the curriculum test paper
      const assessmentData = {
        class_id: selectedClass.id,
        title: assessmentFromCurriculum.title,
        total_marks: assessmentFromCurriculum.totalMarks,
        term: formattedTerm,
        category: 'Test',
        description: `Created from curriculum: ${assessmentFromCurriculum.subject} - ${assessmentFromCurriculum.year}`,
        // Auto-sync metadata for Grade Book integration
        auto_sync_enabled: assessmentFromCurriculum.autoSync || false,
        sync_config: assessmentFromCurriculum.syncConfig ? JSON.stringify(assessmentFromCurriculum.syncConfig) : null,
        source: 'curriculum',
        test_id: assessmentFromCurriculum.testId
      };

      const createdAssessment = await apiService.createAssessment(assessmentData);
      
      // If auto-sync is enabled, store answer key for automatic grading
      if (assessmentFromCurriculum.autoSync && assessmentFromCurriculum.syncConfig?.answerKey) {
        localStorage.setItem(`assessment_answerkey_${createdAssessment?.id || assessmentFromCurriculum.testId}`, 
          JSON.stringify(assessmentFromCurriculum.syncConfig.answerKey));
      }

      toast.success(`Assessment "${assessmentFromCurriculum.title}" created successfully!`, {
        description: assessmentFromCurriculum.autoSync 
          ? 'Auto-sync enabled. Enter student marks to auto-calculate grades.' 
          : 'You can now enter student grades.'
      });
      
      setCurriculumAssessmentCreated(true);
      setShowCurriculumBanner(false);
      
      // Clear pending assessment from localStorage
      localStorage.removeItem('pendingCurriculumAssessment');
      
      // Clear navigation state to prevent re-showing banner on refresh
      window.history.replaceState({}, document.title);
      
      // Refresh assessments list
      const matchedClass = selectedClass;
      if (matchedClass) {
        const allAssessmentsData = await apiService.listAssessments(matchedClass.id);
        let filteredAssessments = allAssessmentsData || [];
        if (selectedTerm !== "All Terms" && filteredAssessments.length > 0) {
          filteredAssessments = filteredAssessments.filter(a => a.term === selectedTerm);
        }
        setAssessments(filteredAssessments);
      }
    } catch (error) {
      console.error('Error creating assessment:', error);
      toast.error('Failed to create assessment. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 -m-6 p-6">
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Curriculum Assessment Banner */}
        {showCurriculumBanner && assessmentFromCurriculum && (
          <Card className="border-2 border-blue-500 bg-blue-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-blue-900">Create Assessment from Curriculum</h3>
                      {assessmentFromCurriculum.autoSync && (
                        <Badge className="bg-green-100 text-green-700 text-xs">Auto-Sync Enabled</Badge>
                      )}
                    </div>
                    <p className="text-sm text-blue-700">
                      <strong>{assessmentFromCurriculum.title}</strong> • {assessmentFromCurriculum.totalMarks} marks • {assessmentFromCurriculum.duration}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {assessmentFromCurriculum.subject} - {assessmentFromCurriculum.year} - {assessmentFromCurriculum.term}
                      {assessmentFromCurriculum.syncConfig && (
                        <span className="ml-2">• Weight: {assessmentFromCurriculum.syncConfig.weightPercentage}%</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleCreateCurriculumAssessment}
                    disabled={!selectedClass}
                    data-testid="confirm-create-assessment"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Create Assessment
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => {
                      setShowCurriculumBanner(false);
                      localStorage.removeItem('pendingCurriculumAssessment');
                      window.history.replaceState({}, document.title);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {!selectedClass && (
                <p className="text-sm text-amber-600 mt-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Please select a class below before creating the assessment
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Success Message after creating assessment */}
        {curriculumAssessmentCreated && (
          <Card className="border-2 border-green-500 bg-green-50">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">
                  Assessment created successfully! You can now enter grades for this assessment in the table below.
                </span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setCurriculumAssessmentCreated(false)}
                  className="ml-auto"
                >
                  Dismiss
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Grade Book
            </h1>
            <p className="text-muted-foreground mt-1">
              Professional grade management system for {currentUser?.name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="default" 
              className="gap-2 bg-green-600 hover:bg-green-700 text-white"
              onClick={() => {
                setBatchGrades({});
                setBatchCustomName('');
                setBatchTotalScore('');
                setBatchEntryOpen(true);
              }}
              disabled={students.length === 0}
            >
              <ClipboardList className="h-4 w-4" />
              Batch Entry
            </Button>
            <Button 
              variant="outline" 
              className="gap-2 bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
              onClick={() => setBlueprintOpen(true)}
            >
              <BookOpen className="h-4 w-4" />
              Assessment Blueprint
            </Button>
            <Button variant="outline" className="gap-2">
              <Printer className="h-4 w-4" />
              Print Grade Book
            </Button>
            {/* Print All Reports - Principal Only */}
            {currentUser?.role === 'principal' && (
              <Button 
                variant="outline" 
                className="gap-2 bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
                onClick={handleBulkPrintReportCards}
                disabled={students.length === 0 || bulkPrintLoading}
                title="Principal only: Print all student report cards at once"
              >
                {bulkPrintLoading ? (
                  <div className="h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                {bulkPrintLoading ? 'Generating...' : `Print All Reports (${students.length})`}
              </Button>
            )}
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Class Selection</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Year Level</label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year}>Year {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Subject</label>
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map(subject => (
                    <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Term</label>
              <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Term 1">Term 1</SelectItem>
                  <SelectItem value="Term 2">Term 2</SelectItem>
                  <SelectItem value="Term 3">Term 3</SelectItem>
                  <SelectItem value="Term 4">Term 4</SelectItem>
                  <SelectItem value="All Terms">All Terms</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Teacher selector for principals with multiple classes */}
            {currentUser?.role === 'principal' && availableTeachers.length > 1 && (
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Teacher</label>
                <Select value={selectedTeacherId || ''} onValueChange={(val) => setSelectedTeacherId(val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select teacher" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTeachers.map(tc => (
                      <SelectItem key={tc.teacher_id || tc.id} value={tc.teacher_id || tc.id}>
                        {tc.teacher_name || tc.teacher_id || 'Unknown'} ({tc.student_count || 0} students)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Traffic Light Summary Card */}
        {students.length > 0 && (
          <Card className="border-2 border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Class Performance Overview
              </CardTitle>
              <CardDescription>Traffic light indicators showing student performance status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Green - Good Performance */}
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {trafficLightStats.green}
                  </div>
                  <div>
                    <div className="font-semibold text-green-800">On Track</div>
                    <div className="text-sm text-green-600">70%+ Score</div>
                    <div className="text-xs text-green-500 mt-1">
                      {trafficLightStats.total > 0 ? Math.round((trafficLightStats.green / trafficLightStats.total) * 100) : 0}% of class
                    </div>
                  </div>
                </div>

                {/* Yellow - Needs Attention */}
                <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {trafficLightStats.yellow}
                  </div>
                  <div>
                    <div className="font-semibold text-yellow-800">Needs Support</div>
                    <div className="text-sm text-yellow-600">50-69% Score</div>
                    <div className="text-xs text-yellow-500 mt-1">
                      {trafficLightStats.total > 0 ? Math.round((trafficLightStats.yellow / trafficLightStats.total) * 100) : 0}% of class
                    </div>
                  </div>
                </div>

                {/* Red - At Risk */}
                <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {trafficLightStats.red}
                  </div>
                  <div>
                    <div className="font-semibold text-red-800">Urgent Action</div>
                    <div className="text-sm text-red-600">Below 50%</div>
                    <div className="text-xs text-red-500 mt-1">
                      {trafficLightStats.total > 0 ? Math.round((trafficLightStats.red / trafficLightStats.total) * 100) : 0}% of class
                    </div>
                  </div>
                </div>

                {/* Class Summary */}
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-blue-800">Total Students</div>
                    <div className="text-sm text-blue-600">{students.length} enrolled</div>
                    <div className="text-xs text-blue-500 mt-1">
                      Avg GPA: {calculateClassGPA()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar Visualization */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-700">Class Distribution</span>
                </div>
                <div className="flex h-6 rounded-full overflow-hidden bg-gray-200">
                  {trafficLightStats.total > 0 && (
                    <>
                      <div 
                        className="bg-green-500 flex items-center justify-center text-xs text-white font-medium transition-all"
                        style={{ width: `${(trafficLightStats.green / trafficLightStats.total) * 100}%` }}
                      >
                        {trafficLightStats.green > 0 && `${Math.round((trafficLightStats.green / trafficLightStats.total) * 100)}%`}
                      </div>
                      <div 
                        className="bg-yellow-500 flex items-center justify-center text-xs text-white font-medium transition-all"
                        style={{ width: `${(trafficLightStats.yellow / trafficLightStats.total) * 100}%` }}
                      >
                        {trafficLightStats.yellow > 0 && `${Math.round((trafficLightStats.yellow / trafficLightStats.total) * 100)}%`}
                      </div>
                      <div 
                        className="bg-red-500 flex items-center justify-center text-xs text-white font-medium transition-all"
                        style={{ width: `${(trafficLightStats.red / trafficLightStats.total) * 100}%` }}
                      >
                        {trafficLightStats.red > 0 && `${Math.round((trafficLightStats.red / trafficLightStats.total) * 100)}%`}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>🟢 Good</span>
                  <span>🟡 At Risk</span>
                  <span>🔴 Failing</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Grade Table */}
        <Card>
          <CardHeader>
            {/* Excel-style Header Info - Primary Schools */}
            {isPrimarySchool() && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 mb-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-green-800">Room:</span>
                    <span className="text-green-700">{selectedClass?.name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-green-800">Teacher:</span>
                    <span className="text-green-700">{selectedClass?.teacher_name || currentUser?.name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-green-800">Class:</span>
                    <span className="text-green-700">Year {selectedYear}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Excel-style Header Info - Secondary Schools */}
            {isSecondarySchool() && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-lg font-bold text-blue-800">
                    Gradebook YEAR {selectedYear}{selectedClass?.name?.match(/[A-Z]$/)?.[0] || ''} {selectedSubject}
                  </div>
                  <Badge className="bg-blue-600 text-white px-3 py-1">
                    {selectedTerm} {new Date().getFullYear()}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-800">Room:</span>
                    <span className="text-blue-700">{selectedClass?.name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-800">Teacher:</span>
                    <span className="text-blue-700">{selectedClass?.teacher_name || currentUser?.name || '-'}</span>
                  </div>
                  {getCurrentStrandInfo() && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-blue-800">Strand/Topic:</span>
                        <span className="text-blue-700 text-xs">{getCurrentStrandInfo()?.strands?.slice(0, 2).join(', ')}...</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-blue-800">Display:</span>
                        <Badge variant="outline" className="bg-blue-100 text-blue-700 text-xs">CA</Badge>
                      </div>
                    </>
                  )}
                </div>
                {getCurrentStrandInfo() && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <span className="text-xs text-blue-600 font-medium">Sub-Topics: </span>
                    <span className="text-xs text-blue-500">{getCurrentStrandInfo()?.subTopics?.slice(0, 4).join(' • ')}...</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  Year {selectedYear} {selectedSubject}
                  {selectedClass && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedClass.name}
                    </Badge>
                  )}
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                    {selectedTerm}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {students.length > 0 ? (
                    <>
                      <span className="font-medium text-primary">{students.length} students</span> enrolled · {assessments.length} assessments ({selectedTerm})
                    </>
                  ) : (
                    <span className="text-muted-foreground">No students enrolled in this class</span>
                  )}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {/* View Toggle Button */}
                <Button
                  variant={compactView ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCompactView(!compactView)}
                  className="text-xs"
                >
                  {compactView ? "📊 Strand View" : "📋 Detailed View"}
                </Button>
                <Badge variant="outline" className="text-sm">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Click cell to edit
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Compact Strand-Based Table View */}
            {compactView ? (
              <div className="rounded-lg border overflow-x-auto bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-indigo-100 to-purple-100">
                      <TableHead className="font-bold sticky left-0 z-10 min-w-[200px] bg-indigo-100 border-r-2">
                        STUDENT NAME
                      </TableHead>
                      {/* Dynamic Strand Columns from Database Assessments */}
                      {(() => {
                        const processedGrades = getProcessedGrades();
                        const caAssessments = processedGrades?.caAssessments || [];
                        // Group assessments by strand and type
                        const strandGroups = {};
                        caAssessments.forEach(assessment => {
                          const key = `${assessment.strand || 'General'}-${assessment.type}`;
                          if (!strandGroups[key]) {
                            strandGroups[key] = {
                              strand: assessment.strand,
                              type: assessment.type,
                              title: assessment.title,
                              maxMarks: assessment.maxMarks
                            };
                          }
                        });
                        
                        const sortedKeys = Object.keys(strandGroups).sort();
                        
                        // Only show default placeholder columns if NO database assessments exist
                        // When database assessments exist, only show those (the dynamic green columns)
                        if (sortedKeys.length === 0) {
                          // No database assessments - show a message or minimal placeholder
                          return (
                            <TableHead className="text-center text-xs font-semibold min-w-[120px] bg-gray-50 border-r">
                              <div className="flex flex-col">
                                <span className="text-gray-500">No Assessments</span>
                                <span className="text-[10px] text-gray-400">Add via Batch Entry</span>
                              </div>
                            </TableHead>
                          );
                        }
                        
                        // Show dynamic columns from database assessments
                        return sortedKeys.map((key, idx) => {
                          const group = strandGroups[key];
                          const bgColor = group.type === 'Quiz' ? 'bg-indigo-50' : 
                                          group.type === 'Test' ? 'bg-emerald-50' :
                                          group.type === 'Project' ? 'bg-amber-50' : 'bg-slate-50';
                          const textColor = group.type === 'Quiz' ? 'text-indigo-700' : 
                                            group.type === 'Test' ? 'text-emerald-700' :
                                            group.type === 'Project' ? 'text-amber-700' : 'text-slate-700';
                          const strandLabel = group.strand ? group.strand.replace(/strand\s*/i, 'S') : group.type?.charAt(0) || '#';
                          return (
                            <TableHead key={key} className={`text-center text-xs font-semibold min-w-[60px] ${bgColor} border-r`}>
                              <div className="flex flex-col">
                                <span className={`${textColor} font-bold`}>{strandLabel}</span>
                                <span className="text-[10px] text-gray-500">{group.type}</span>
                              </div>
                            </TableHead>
                          );
                        });
                      })()}
                      <TableHead className="text-center font-bold min-w-[70px] bg-purple-100 border-r-2">
                        <div className="flex flex-col">
                          <span className="text-purple-800">CA</span>
                          <span className="text-[10px]">40%</span>
                        </div>
                      </TableHead>
                      <TableHead className="text-center font-bold min-w-[70px] bg-red-100 border-r">
                        <div className="flex flex-col">
                          <span className="text-red-800">EXAM</span>
                          <span className="text-[10px]">60%</span>
                        </div>
                      </TableHead>
                      <TableHead className="text-center font-bold min-w-[70px] bg-emerald-100 border-r">
                        <div className="flex flex-col">
                          <span className="text-emerald-800">TOTAL</span>
                          <span className="text-[10px]">100%</span>
                        </div>
                      </TableHead>
                      <TableHead className="text-center font-bold min-w-[60px] bg-yellow-100">
                        GRADE
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={20} className="text-center py-12">
                          <div className="flex flex-col items-center gap-2 text-gray-500">
                            <Users className="h-12 w-12 text-gray-300" />
                            <p className="font-medium">No Students Found</p>
                            <p className="text-sm">Select a class from the dropdown above to view students</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      students.map((student, studentIdx) => {
                        const processedGrades = getProcessedGrades();
                        const caAssessments = processedGrades?.caAssessments || [];
                        const studentGrades = processedGrades?.studentGrades || {};
                        const studentGradeData = studentGrades[student.id] || {};
                        
                        // Calculate totals
                        const caResult = processedGrades ? calculateDBCATotal(student.id, processedGrades) : { scaledScore: 0 };
                        const examResult = processedGrades ? getDBExamScore(student.id, processedGrades) : { scaledScore: 0 };
                        const caTotal = caResult?.scaledScore || 0;
                        const examScore = examResult?.scaledScore || 0;
                        const finalTotal = caTotal + examScore;
                        const grade = getLetterGrade(finalTotal);
                        
                        // Get TL status
                        const tlStatus = getTrafficLightStatus(finalTotal);
                        const tlColors = {
                          green: 'bg-green-500',
                          yellow: 'bg-yellow-500',
                          red: 'bg-red-500'
                        };
                        
                        // Group assessments
                        const strandGroups = {};
                        caAssessments.forEach(assessment => {
                          const key = `${assessment.strand || 'General'}-${assessment.type}`;
                          if (!strandGroups[key]) {
                            strandGroups[key] = assessment;
                          }
                        });
                        const sortedKeys = Object.keys(strandGroups).sort();
                        
                        return (
                          <TableRow key={student.id} className={studentIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <TableCell className="sticky left-0 z-10 font-medium border-r-2 bg-inherit">
                              <div className="flex items-center gap-2">
                                <div className={`w-2.5 h-2.5 rounded-full ${tlColors[tlStatus]}`} title={`${tlStatus === 'green' ? 'On Track' : tlStatus === 'yellow' ? 'Needs Support' : 'Urgent Action'}`} />
                                <span className="truncate max-w-[150px]">{student.name}</span>
                              </div>
                            </TableCell>
                            {/* Strand Assessment Scores */}
                            {sortedKeys.length === 0 ? (
                              // Single cell when no assessments
                              <TableCell className="text-center text-sm border-r bg-gray-50">
                                <span className="text-gray-400">-</span>
                              </TableCell>
                            ) : (
                              sortedKeys.map((key) => {
                                const assessment = strandGroups[key];
                                const gradeData = studentGradeData[assessment.title];
                                const marks = gradeData?.marks;
                                const maxMarks = assessment.maxMarks || 10;
                                
                                return (
                                  <TableCell key={key} className="text-center text-sm border-r">
                                    {marks !== undefined ? (
                                      <span className={marks >= maxMarks * 0.7 ? 'text-green-600 font-medium' : marks >= maxMarks * 0.5 ? 'text-amber-600' : 'text-red-600'}>
                                        {marks}
                                      </span>
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </TableCell>
                                );
                              })
                            )}
                            <TableCell className="text-center font-semibold border-r-2 bg-purple-50">
                              <span className="text-purple-700">{caTotal?.toFixed(1) || '-'}</span>
                            </TableCell>
                            <TableCell className="text-center font-semibold border-r bg-red-50">
                              <span className="text-red-700">{examScore?.toFixed(1) || '-'}</span>
                            </TableCell>
                            <TableCell className="text-center font-bold border-r bg-emerald-50">
                              <span className="text-emerald-700">{finalTotal?.toFixed(1) || '-'}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={`${grade === 'A+' || grade === 'A' ? 'bg-green-600' : grade === 'B+' || grade === 'B' ? 'bg-blue-600' : grade === 'C+' || grade === 'C' ? 'bg-amber-600' : 'bg-red-600'}`}>
                                {grade}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                    {/* Class Average Row */}
                    {students.length > 0 && (
                      <TableRow className="bg-gray-100 font-bold border-t-2">
                        <TableCell className="sticky left-0 z-10 bg-gray-100 border-r-2">CLASS AVERAGE</TableCell>
                        {(() => {
                          const processedGrades = getProcessedGrades();
                          const caAssessments = processedGrades?.caAssessments || [];
                          const strandGroups = {};
                          caAssessments.forEach(a => {
                            const key = `${a.strand || 'General'}-${a.type}`;
                            if (!strandGroups[key]) strandGroups[key] = a;
                          });
                          const keys = Object.keys(strandGroups).sort();
                          return keys.length === 0 ? 
                            [...Array(8)].map((_, i) => <TableCell key={i} className="text-center border-r">-</TableCell>) :
                            keys.map(k => <TableCell key={k} className="text-center border-r">-</TableCell>);
                        })()}
                        <TableCell className="text-center border-r-2 bg-purple-50">
                          <span className="text-purple-700">
                            {(() => {
                              if (students.length === 0) return '-';
                              const pg = getProcessedGrades();
                              if (!pg) return '-';
                              const avg = students.reduce((sum, s) => sum + (calculateDBCATotal(s.id, pg)?.scaledScore || 0), 0) / students.length;
                              return avg.toFixed(1);
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="text-center border-r bg-red-50">
                          <span className="text-red-700">
                            {(() => {
                              if (students.length === 0) return '-';
                              const pg = getProcessedGrades();
                              if (!pg) return '-';
                              const avg = students.reduce((sum, s) => sum + (getDBExamScore(s.id, pg)?.scaledScore || 0), 0) / students.length;
                              return avg.toFixed(1);
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="text-center border-r bg-emerald-50">
                          <span className="text-emerald-700">
                            {(() => {
                              if (students.length === 0) return '-';
                              const pg = getProcessedGrades();
                              if (!pg) return '-';
                              const avg = students.reduce((sum, s) => {
                                const ca = calculateDBCATotal(s.id, pg)?.scaledScore || 0;
                                const ex = getDBExamScore(s.id, pg)?.scaledScore || 0;
                                return sum + ca + ex;
                              }, 0) / students.length;
                              return avg.toFixed(1);
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">-</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            ) : (
            /* Original Detailed Table View */
            <div className="rounded-lg border overflow-x-auto bg-white">
              <Table>
                <TableHeader>
                  {/* First Header Row - Assessment Names (Rotated) */}
                  <TableRow className="bg-white border-b-2 border-gray-300">
                    <TableHead 
                      rowSpan={2} 
                      className="font-bold sticky left-0 z-10 min-w-[180px] text-center align-middle border-r-2 border-gray-300 bg-slate-800 text-white"
                    >
                      STUDENT NAME
                    </TableHead>
                    
                    {/* Fixed Assessment Type Columns - Only for Year 13 ICT */}
                    {isYear13ICT() && (
                      <>
                        <TableHead className="text-center font-bold min-w-[50px] max-w-[50px] p-1 border-r border-gray-200 bg-indigo-100">
                          <div className="flex items-center justify-center h-36">
                            <div className="transform -rotate-[55deg] whitespace-nowrap">
                              <span className="text-[10px] font-bold text-indigo-800">Quiz</span>
                            </div>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-bold min-w-[50px] max-w-[50px] p-1 border-r border-gray-200 bg-amber-100">
                          <div className="flex items-center justify-center h-36">
                            <div className="transform -rotate-[55deg] whitespace-nowrap">
                              <span className="text-[10px] font-bold text-amber-800">Homework</span>
                            </div>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-bold min-w-[50px] max-w-[50px] p-1 border-r border-gray-200 bg-teal-100">
                          <div className="flex items-center justify-center h-36">
                            <div className="transform -rotate-[55deg] whitespace-nowrap">
                              <span className="text-[10px] font-bold text-teal-800">Practical</span>
                            </div>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-bold min-w-[50px] max-w-[50px] p-1 border-r border-gray-200 bg-rose-100">
                          <div className="flex items-center justify-center h-36">
                            <div className="transform -rotate-[55deg] whitespace-nowrap">
                              <span className="text-[10px] font-bold text-rose-800">Project</span>
                            </div>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-bold min-w-[50px] max-w-[50px] p-1 border-r border-gray-200 bg-violet-100">
                          <div className="flex items-center justify-center h-36">
                            <div className="transform -rotate-[55deg] whitespace-nowrap">
                              <span className="text-[10px] font-bold text-violet-800">Test</span>
                            </div>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-bold min-w-[50px] max-w-[50px] p-1 border-r border-gray-200 bg-emerald-100">
                          <div className="flex items-center justify-center h-36">
                            <div className="transform -rotate-[55deg] whitespace-nowrap">
                              <span className="text-[10px] font-bold text-emerald-800">Exercise</span>
                            </div>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-bold min-w-[50px] max-w-[50px] p-1 border-r-2 border-gray-300 bg-orange-100">
                          <div className="flex items-center justify-center h-36">
                            <div className="transform -rotate-[55deg] whitespace-nowrap">
                              <span className="text-[10px] font-bold text-orange-800">Assignment</span>
                            </div>
                          </div>
                        </TableHead>
                      </>
                    )}
                    
                    {/* Primary and Secondary schools both use dynamic assessments from database */}
                    
                    {assessments.map((assessment) => {
                      const assessmentType = getAssessmentTypeFromTitle(assessment.title);
                      const shortTitle = assessment.title
                        .replace(/^YEAR\s+\d+\s+\w+:\s*/i, '')
                        .replace(/\s*\([^)]+\)\s*$/, '');
                      const displayTitle = shortTitle.length > 30 ? shortTitle.substring(0, 28) + '...' : shortTitle;
                      return (
                        <TableHead 
                          key={assessment.id} 
                          className={`text-center font-bold min-w-[50px] max-w-[50px] p-1 border-r border-gray-200 cursor-pointer hover:brightness-95 transition-all relative ${assessmentType.color || 'bg-slate-50'}`}
                          onClick={() => setBlueprintOpen(true)}
                          title={assessment.title}
                        >
                          <div className="flex items-center justify-center h-36">
                            <div className="transform -rotate-[55deg] whitespace-nowrap origin-center">
                              <span className={`text-[10px] font-semibold ${assessmentType.textColor || 'text-slate-700'}`}>
                                {displayTitle}
                              </span>
                            </div>
                          </div>
                        </TableHead>
                      );
                    })}
                    <TableHead className="text-center font-bold min-w-[60px] border-r border-gray-200 bg-slate-700">
                      <div className="flex flex-col items-center justify-center py-2">
                        <span className="text-[10px] font-bold text-white tracking-wide">CA</span>
                        <span className="text-[9px] text-slate-300">TOTAL</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center font-bold min-w-[60px] border-r border-gray-200 bg-rose-700">
                      <div className="flex flex-col items-center justify-center py-2">
                        <span className="text-[10px] font-bold text-white tracking-wide">EXAM</span>
                        <span className="text-[9px] text-rose-200">60%</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center font-bold min-w-[60px] border-r border-gray-200 bg-emerald-700">
                      <div className="flex flex-col items-center justify-center py-2">
                        <span className="text-[10px] font-bold text-white tracking-wide">TOTAL</span>
                        <span className="text-[9px] text-emerald-200">100%</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center font-bold min-w-[50px] border-r border-gray-200 bg-amber-600">
                      <div className="flex flex-col items-center justify-center py-2">
                        <span className="text-[10px] font-bold text-white tracking-wide">RANK</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center font-bold min-w-[50px] border-r border-gray-200 bg-teal-700">
                      <div className="flex flex-col items-center justify-center py-2">
                        <span className="text-[10px] font-bold text-white tracking-wide">GRADE</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center font-bold min-w-[50px] border-r border-gray-200 bg-indigo-700">
                      <div className="flex flex-col items-center justify-center py-2">
                        <span className="text-[10px] font-bold text-white tracking-wide">GPA</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center font-bold min-w-[70px] bg-violet-700">
                      <div className="flex flex-col items-center justify-center py-2">
                        <span className="text-[10px] font-bold text-white tracking-wide">REPORT</span>
                        <span className="text-[9px] text-violet-200">CARD</span>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={100} className="text-center py-12">
                        <div className="flex flex-col items-center gap-4">
                          <div className="rounded-full bg-muted p-4">
                            <Users className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold mb-1">No Students Found</h3>
                            <p className="text-sm text-muted-foreground">
                              {selectedClass ? (
                                `No students are enrolled in ${selectedClass.name}`
                              ) : (
                                `Select a class from the dropdown above to view students`
                              )}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    students.map((student, idx) => {
                    const stats = getStudentStats(student.id);
                    return (
                      <TableRow 
                        key={student.id}
                        className={`hover:bg-blue-50/50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-200`}
                      >
                        <TableCell className="sticky left-0 z-10 font-medium border-r-2 border-gray-300 p-3" style={{backgroundColor: '#C2BEF8'}}>
                          <div className="flex items-center gap-2">
                            {/* Traffic Light Indicator */}
                            <div 
                              className={`w-3 h-3 rounded-full ${getTrafficLight(stats.finalScore).color} shadow-sm`}
                              title={`${getTrafficLight(stats.finalScore).label}: ${getTrafficLight(stats.finalScore).description}`}
                            />
                            <div>
                              <div className="text-sm font-medium">{student.name}</div>
                              <div className="text-xs text-gray-500">#{stats.rank}</div>
                            </div>
                          </div>
                        </TableCell>
                        
                        {/* Fixed Assessment Type Data Cells - Only for Year 13 ICT */}
                        {isYear13ICT() && (
                          <>
                            {/* Quiz */}
                            <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: getAssessmentMark(student.id, 'quiz') ? '#E8FFE5' : '#F5F3FF'}}>
                              <div className="flex flex-col items-center">
                                <Input
                                  type="number"
                                  value={getAssessmentMark(student.id, 'quiz') ?? ''}
                                  onChange={(e) => updateAssessmentMark(student.id, 'quiz', e.target.value)}
                                  placeholder="___"
                                  className="h-7 w-14 text-center text-sm border-indigo-200 mx-auto font-bold"
                                  max={15}
                                  min={0}
                                />
                                <span className="text-xs text-indigo-600 mt-1">/15</span>
                              </div>
                            </TableCell>
                            {/* Homework */}
                            <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: getAssessmentMark(student.id, 'homework') ? '#E8FFE5' : '#FFFBEB'}}>
                              <div className="flex flex-col items-center">
                                <Input
                                  type="number"
                                  value={getAssessmentMark(student.id, 'homework') ?? ''}
                                  onChange={(e) => updateAssessmentMark(student.id, 'homework', e.target.value)}
                                  placeholder="___"
                                  className="h-7 w-14 text-center text-sm border-amber-200 mx-auto font-bold"
                                  max={10}
                                  min={0}
                                />
                                <span className="text-xs text-amber-600 mt-1">/10</span>
                              </div>
                            </TableCell>
                            {/* Practical */}
                            <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: getAssessmentMark(student.id, 'practical') ? '#E8FFE5' : '#F0FDFA'}}>
                              <div className="flex flex-col items-center">
                                <Input
                                  type="number"
                                  value={getAssessmentMark(student.id, 'practical') ?? ''}
                                  onChange={(e) => updateAssessmentMark(student.id, 'practical', e.target.value)}
                                  placeholder="___"
                                  className="h-7 w-14 text-center text-sm border-teal-200 mx-auto font-bold"
                                  max={25}
                                  min={0}
                                />
                                <span className="text-xs text-teal-600 mt-1">/25</span>
                              </div>
                            </TableCell>
                            {/* Project */}
                            <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: getAssessmentMark(student.id, 'project') ? '#E8FFE5' : '#FFF1F2'}}>
                              <div className="flex flex-col items-center">
                                <Input
                                  type="number"
                                  value={getAssessmentMark(student.id, 'project') ?? ''}
                                  onChange={(e) => updateAssessmentMark(student.id, 'project', e.target.value)}
                                  placeholder="___"
                                  className="h-7 w-14 text-center text-sm border-rose-200 mx-auto font-bold"
                                  max={30}
                                  min={0}
                                />
                                <span className="text-xs text-rose-600 mt-1">/30</span>
                              </div>
                            </TableCell>
                            {/* Test */}
                            <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: getAssessmentMark(student.id, 'test') ? '#E8FFE5' : '#F5F3FF'}}>
                              <div className="flex flex-col items-center">
                                <Input
                                  type="number"
                                  value={getAssessmentMark(student.id, 'test') ?? ''}
                                  onChange={(e) => updateAssessmentMark(student.id, 'test', e.target.value)}
                                  placeholder="___"
                                  className="h-7 w-14 text-center text-sm border-violet-200 mx-auto font-bold"
                                  max={20}
                                  min={0}
                                />
                                <span className="text-xs text-violet-600 mt-1">/20</span>
                              </div>
                            </TableCell>
                            {/* Exercise */}
                            <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: getAssessmentMark(student.id, 'exercise') ? '#E8FFE5' : '#ECFDF5'}}>
                              <div className="flex flex-col items-center">
                                <Input
                                  type="number"
                                  value={getAssessmentMark(student.id, 'exercise') ?? ''}
                                  onChange={(e) => updateAssessmentMark(student.id, 'exercise', e.target.value)}
                                  placeholder="___"
                                  className="h-7 w-14 text-center text-sm border-emerald-200 mx-auto font-bold"
                                  max={20}
                                  min={0}
                                />
                                <span className="text-xs text-emerald-600 mt-1">/20</span>
                              </div>
                            </TableCell>
                            {/* Assignment */}
                            <TableCell className="text-center border-r-2 border-gray-300 p-2" style={{backgroundColor: getAssessmentMark(student.id, 'assignment') ? '#E8FFE5' : '#FFFBEB'}}>
                              <div className="flex flex-col items-center">
                                <Input
                                  type="number"
                                  value={getAssessmentMark(student.id, 'assignment') ?? ''}
                                  onChange={(e) => updateAssessmentMark(student.id, 'assignment', e.target.value)}
                                  placeholder="___"
                                  className="h-7 w-14 text-center text-sm border-yellow-200 mx-auto font-bold"
                                  max={20}
                                  min={0}
                                />
                                <span className="text-xs text-yellow-600 mt-1">/20</span>
                              </div>
                            </TableCell>
                          </>
                        )}
                        
                        {/* All schools use dynamic assessments from database */}
                        
                        {assessments.map(assessment => {
                          const grade = getGradeForCell(student.id, assessment.id);
                          const isEditing = editingCell?.studentId === student.id &&
                                          editingCell?.assessmentId === assessment.id;
                          
                          return (
                            <TableCell 
                              key={assessment.id} 
                              className="text-center cursor-pointer hover:bg-green-100 transition-colors border-r border-gray-200 p-2"
                              style={{backgroundColor: grade !== null ? '#E8FFE5' : '#FFFEF0'}}
                              onClick={() => !isEditing && handleCellClick(student.id, assessment.id, grade)}
                              title={`Click to enter raw marks (max: ${assessment.total_marks})`}
                            >
                              {isEditing ? (
                                <div className="flex gap-1 justify-center items-center">
                                  <Input
                                    type="number"
                                    value={tempValue}
                                    onChange={(e) => setTempValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveGrade();
                                      if (e.key === 'Escape') setEditingCell(null);
                                    }}
                                    className="h-7 w-14 text-center text-sm border-green-400"
                                    placeholder={`/${assessment.total_marks}`}
                                    autoFocus
                                    max={assessment.total_marks}
                                    min={0}
                                  />
                                  <Button size="sm" onClick={handleSaveGrade} className="h-7 w-7 p-0 bg-green-600 hover:bg-green-700">
                                    <Save className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center">
                                  <span className={`text-sm ${grade !== null ? 'font-bold text-green-800' : 'text-gray-400 italic'}`}>
                                    {grade !== null ? parseFloat(grade).toFixed(0) : '___'}
                                  </span>
                                  <span className="text-xs text-gray-500">/{assessment.total_marks}</span>
                                </div>
                              )}
                            </TableCell>
                          );
                        })}
                        {/* CA Total - calculated from Quiz + Homework + Practical + Project + Test */}
                        <TableCell className="text-center font-semibold bg-blue-50 text-blue-900 border-r border-gray-200 p-2 text-sm">
                          <div className="flex flex-col items-center">
                            <span className="text-sm font-bold">{stats.caTotal.toFixed(1)}</span>
                            <span className="text-xs text-blue-600">/40</span>
                          </div>
                        </TableCell>
                        {/* Exam (60%) - calculated weighted value */}
                        <TableCell className="text-center font-semibold bg-red-100 text-red-900 border-r border-gray-200 p-2 text-sm">
                          <div className="flex flex-col items-center">
                            <span className="text-sm font-bold">{stats.examScore.toFixed(1)}</span>
                            <span className="text-xs text-red-600">/60</span>
                          </div>
                        </TableCell>
                        {/* Total (100%) = CA + Exam */}
                        <TableCell className="text-center font-bold bg-yellow-50 text-yellow-900 border-r border-gray-200 p-2">
                          {stats.finalScore.toFixed(1)}
                        </TableCell>
                        {/* Ranking */}
                        <TableCell className="text-center font-bold bg-amber-50 text-amber-900 border-r border-gray-200 p-2">
                          {stats.rank}
                        </TableCell>
                        {/* Letter Grade */}
                        <TableCell className="text-center border-r border-gray-200 p-2">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            stats.finalScore >= 80 ? 'bg-green-100 text-green-800' :
                            stats.finalScore >= 70 ? 'bg-blue-100 text-blue-800' :
                            stats.finalScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            stats.finalScore >= 50 ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {getLetterGrade(stats.finalScore)}
                          </span>
                        </TableCell>
                        {/* GPA */}
                        <TableCell className="text-center font-semibold bg-orange-50 text-orange-900 border-r border-gray-200 p-2 text-sm">
                          {stats.gpa.toFixed(2)}
                        </TableCell>
                        {/* Report Card Button */}
                        <TableCell className="text-center bg-pink-50 p-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenReport(student)}
                            className="hover:bg-pink-100 h-7 px-2"
                          >
                            <FileText className="h-4 w-4 text-pink-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                  )}
                  
                  {/* Class Average Row */}
                  <TableRow className="bg-orange-100 border-t-2 border-gray-400 font-bold">
                    <TableCell className="sticky left-0 z-10 border-r-2 border-gray-300 p-3" style={{backgroundColor: '#C2BEF8'}}>
                      <span className="text-sm font-bold text-gray-800">CLASS AVERAGE</span>
                    </TableCell>
                    {/* Empty cells for the 7 fixed assessment columns - Only for Year 13 ICT */}
                    {isYear13ICT() && (
                      <>
                        <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: '#F5F3FF'}}>
                          <span className="text-xs text-gray-400">-</span>
                        </TableCell>
                        <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: '#FFFBEB'}}>
                          <span className="text-xs text-gray-400">-</span>
                        </TableCell>
                        <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: '#F0FDFA'}}>
                          <span className="text-xs text-gray-400">-</span>
                        </TableCell>
                        <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: '#FFF1F2'}}>
                          <span className="text-xs text-gray-400">-</span>
                        </TableCell>
                        <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: '#F5F3FF'}}>
                          <span className="text-xs text-gray-400">-</span>
                        </TableCell>
                        <TableCell className="text-center border-r border-gray-200 p-2" style={{backgroundColor: '#ECFDF5'}}>
                          <span className="text-xs text-gray-400">-</span>
                        </TableCell>
                        <TableCell className="text-center border-r-2 border-gray-300 p-2" style={{backgroundColor: '#FFFBEB'}}>
                          <span className="text-xs text-gray-400">-</span>
                        </TableCell>
                      </>
                    )}
                    {assessments.map(assessment => {
                      // Calculate average for this assessment
                      const allGrades = students.map(s => getGradeForCell(s.id, assessment.id)).filter(g => g !== null);
                      const avg = allGrades.length > 0 
                        ? allGrades.reduce((sum, g) => sum + parseFloat(g), 0) / allGrades.length 
                        : 0;
                      return (
                        <TableCell key={assessment.id} className="text-center border-r border-gray-200 p-2">
                          <span className="text-sm font-semibold text-gray-700">
                            {avg > 0 ? avg.toFixed(1) : '-'}
                          </span>
                        </TableCell>
                      );
                    })}
                    {/* CA Total Average */}
                    <TableCell className="text-center bg-blue-100 border-r border-gray-200 p-2">
                      <span className="text-sm font-bold text-blue-900">
                        {studentStatsWithRanking.length > 0 
                          ? (studentStatsWithRanking.reduce((sum, s) => sum + s.caTotal, 0) / studentStatsWithRanking.length).toFixed(1)
                          : '-'}
                      </span>
                    </TableCell>
                    {/* Exam Mark Average - empty for average row */}
                    <TableCell className="text-center bg-red-50 border-r border-gray-200 p-2">
                      <span className="text-xs text-gray-400">-</span>
                    </TableCell>
                    {/* Exam (60%) Average */}
                    <TableCell className="text-center bg-red-100 border-r border-gray-200 p-2">
                      <span className="text-sm font-bold text-red-900">
                        {studentStatsWithRanking.length > 0
                          ? (studentStatsWithRanking.reduce((sum, s) => sum + s.examScore, 0) / studentStatsWithRanking.length).toFixed(1)
                          : '-'}
                      </span>
                    </TableCell>
                    {/* Total (100%) Average */}
                    <TableCell className="text-center bg-yellow-100 border-r border-gray-200 p-2">
                      <span className="text-sm font-bold text-yellow-900">
                        {studentStatsWithRanking.length > 0
                          ? (studentStatsWithRanking.reduce((sum, s) => sum + s.finalScore, 0) / studentStatsWithRanking.length).toFixed(1)
                          : '-'}
                      </span>
                    </TableCell>
                    {/* Ranking - empty for average row */}
                    <TableCell className="text-center bg-amber-50 border-r border-gray-200 p-2">
                      <span className="text-xs text-gray-500">-</span>
                    </TableCell>
                    {/* Grade - empty for average row */}
                    <TableCell className="text-center bg-green-50 border-r border-gray-200 p-2">
                      <span className="text-xs text-gray-500">-</span>
                    </TableCell>
                    {/* GPA Average */}
                    <TableCell className="text-center bg-orange-100 border-r border-gray-200 p-2">
                      <span className="text-sm font-bold text-orange-900">{calculateClassGPA()}</span>
                    </TableCell>
                    {/* Report Card - empty */}
                    <TableCell className="text-center bg-pink-100 p-2">
                      <span className="text-xs text-gray-500">-</span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            )}
            
            {/* Subject Tabs - Excel-style navigation - Primary Schools */}
            {isPrimarySchool() && subjects.length > 0 && (
              <div className="mt-4 flex items-center gap-1 overflow-x-auto pb-2" data-testid="subject-tabs">
                {subjects.map((subject, idx) => {
                  const colors = [
                    { bg: 'bg-green-500', hover: 'hover:bg-green-600', active: 'bg-green-600' },
                    { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', active: 'bg-blue-600' },
                    { bg: 'bg-amber-500', hover: 'hover:bg-amber-600', active: 'bg-amber-600' },
                    { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', active: 'bg-purple-600' },
                    { bg: 'bg-red-500', hover: 'hover:bg-red-600', active: 'bg-red-600' },
                    { bg: 'bg-teal-500', hover: 'hover:bg-teal-600', active: 'bg-teal-600' },
                  ];
                  const color = colors[idx % colors.length];
                  const isActive = selectedSubject === subject;
                  
                  return (
                    <button
                      key={subject}
                      onClick={() => setSelectedSubject(subject)}
                      className={`px-4 py-2 text-xs font-bold text-white rounded-t-md transition-all ${
                        isActive ? color.active + ' shadow-md' : color.bg + ' ' + color.hover + ' opacity-80'
                      }`}
                      data-testid={`subject-tab-${subject.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {subject.length > 3 ? subject.substring(0, 3).toUpperCase() : subject.toUpperCase()}
                    </button>
                  );
                })}
                <div className="flex-1 border-b-2 border-gray-300"></div>
              </div>
            )}
            
            {/* Subject Tabs - Excel-style navigation - Secondary Schools */}
            {isSecondarySchool() && subjects.length > 0 && (
              <div className="mt-4 flex items-center gap-1 overflow-x-auto pb-2" data-testid="secondary-subject-tabs">
                {subjects.map((subject, idx) => {
                  // Excel-like tab colors for secondary subjects
                  const subjectColors = {
                    'Computer Studies': { bg: 'bg-cyan-500', hover: 'hover:bg-cyan-600', active: 'bg-cyan-600' },
                    'ICT': { bg: 'bg-cyan-500', hover: 'hover:bg-cyan-600', active: 'bg-cyan-600' },
                    'Mathematics': { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', active: 'bg-blue-600' },
                    'English': { bg: 'bg-red-500', hover: 'hover:bg-red-600', active: 'bg-red-600' },
                    'Science': { bg: 'bg-green-500', hover: 'hover:bg-green-600', active: 'bg-green-600' },
                    'Social Science': { bg: 'bg-amber-500', hover: 'hover:bg-amber-600', active: 'bg-amber-600' },
                    'French': { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', active: 'bg-purple-600' },
                    'Agriculture': { bg: 'bg-lime-500', hover: 'hover:bg-lime-600', active: 'bg-lime-600' },
                    'Business Studies': { bg: 'bg-orange-500', hover: 'hover:bg-orange-600', active: 'bg-orange-600' },
                    'Religious Education': { bg: 'bg-indigo-500', hover: 'hover:bg-indigo-600', active: 'bg-indigo-600' },
                  };
                  const defaultColors = [
                    { bg: 'bg-slate-500', hover: 'hover:bg-slate-600', active: 'bg-slate-600' },
                    { bg: 'bg-zinc-500', hover: 'hover:bg-zinc-600', active: 'bg-zinc-600' },
                  ];
                  
                  // Find matching subject color or use default
                  const colorKey = Object.keys(subjectColors).find(key => 
                    subject.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(subject.toLowerCase())
                  );
                  const color = colorKey ? subjectColors[colorKey] : defaultColors[idx % defaultColors.length];
                  const isActive = selectedSubject === subject;
                  
                  // Create short label (like Excel: IT, Agri, Eng, BS, etc.)
                  const shortLabels = {
                    'Computer Studies': 'IT',
                    'ICT': 'IT',
                    'Mathematics': 'Math',
                    'English': 'Eng',
                    'Science': 'SC',
                    'Social Science': 'SS',
                    'French': 'Fr',
                    'Agriculture': 'Agri',
                    'Business Studies': 'BS',
                    'Religious Education': 'Rel',
                  };
                  const labelKey = Object.keys(shortLabels).find(key => 
                    subject.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(subject.toLowerCase())
                  );
                  const shortLabel = labelKey ? shortLabels[labelKey] : subject.substring(0, 4);
                  
                  return (
                    <button
                      key={subject}
                      onClick={() => setSelectedSubject(subject)}
                      className={`px-4 py-2 text-xs font-bold text-white rounded-t-md transition-all ${
                        isActive ? color.active + ' shadow-md scale-105' : color.bg + ' ' + color.hover + ' opacity-80'
                      }`}
                      data-testid={`subject-tab-${subject.toLowerCase().replace(/\s+/g, '-')}`}
                      title={subject}
                    >
                      {shortLabel}
                    </button>
                  );
                })}
                {/* Special tabs like Excel sample */}
                <button
                  className="px-3 py-2 text-xs font-bold bg-pink-500 hover:bg-pink-600 text-white rounded-t-md transition-all opacity-80"
                  onClick={() => {
                    if (students.length > 0) {
                      handleOpenReport(students[0]);
                    }
                  }}
                  title="Report Card to Print"
                >
                  📄 Report
                </button>
                <div className="flex-1 border-b-2 border-gray-300"></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Traffic Light Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span>🚦</span>
              Traffic Light Analysis
            </CardTitle>
            <CardDescription>Quick performance overview</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                <div className="text-4xl mb-2">🟢</div>
                <div className="text-2xl font-bold text-green-700">
                  {studentStatsWithRanking.filter(s => s.finalScore >= 70).length}
                </div>
                <div className="text-sm font-medium text-green-600">Good (70%+)</div>
                <div className="text-xs text-green-500 mt-1">On Track</div>
              </div>
              
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 text-center">
                <div className="text-4xl mb-2">🟡</div>
                <div className="text-2xl font-bold text-yellow-700">
                  {studentStatsWithRanking.filter(s => s.finalScore >= 50 && s.finalScore < 70).length}
                </div>
                <div className="text-sm font-medium text-yellow-600">At Risk (50-69%)</div>
                <div className="text-xs text-yellow-500 mt-1">Needs Support</div>
              </div>
              
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center">
                <div className="text-4xl mb-2">🔴</div>
                <div className="text-2xl font-bold text-red-700">
                  {studentStatsWithRanking.filter(s => s.finalScore < 50).length}
                </div>
                <div className="text-sm font-medium text-red-600">Failing (&lt;50%)</div>
                <div className="text-xs text-red-500 mt-1">Urgent Action</div>
              </div>
            </div>

            {/* Action Items */}
            {studentStatsWithRanking.filter(s => s.finalScore < 70).length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-semibold text-amber-800 mb-2">⚠️ Students Requiring Attention:</p>
                <ul className="space-y-1">
                  {studentStatsWithRanking
                    .filter(s => s.finalScore < 70)
                    .slice(0, 3)
                    .map(s => (
                      <li key={s.studentId} className="text-xs text-amber-700 flex items-center gap-2">
                        <span className={getTrafficLight(s.finalScore).icon}></span>
                        <span className="font-medium">{s.studentName}</span>
                        <span className="text-amber-600">({s.finalScore.toFixed(1)}%)</span>
                      </li>
                    ))}
                  {studentStatsWithRanking.filter(s => s.finalScore < 70).length > 3 && (
                    <li className="text-xs text-amber-600 italic">
                      +{studentStatsWithRanking.filter(s => s.finalScore < 70).length - 3} more students
                    </li>
                  )}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Class Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Class Average</p>
                  <p className="text-2xl font-bold">
                    {studentStatsWithRanking.length > 0 
                      ? (studentStatsWithRanking.reduce((sum, s) => sum + s.finalScore, 0) / studentStatsWithRanking.length).toFixed(1) + '%'
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Award className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Class GPA</p>
                  <p className="text-2xl font-bold">{calculateClassGPA()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Award className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">A Grades</p>
                  <p className="text-2xl font-bold">
                    {studentStatsWithRanking.filter(s => s.grade.startsWith('A')).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Students</p>
                  <p className="text-2xl font-bold">{students.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Edit2 className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Assessments</p>
                  <p className="text-2xl font-bold">{assessments.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Assessment Blueprint Dialog */}
        <Dialog open={blueprintOpen} onOpenChange={setBlueprintOpen}>
          <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <span>📋</span>
                Assessment Blueprint Guide
              </DialogTitle>
              <DialogDescription>
                Understanding the assessment structure for {selectedSubject}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-3 gap-4 mt-4">
              {/* Internal Assessments (CA - 40%) */}
              <div className={`${assessmentCategories.internal.color} border-2 border-blue-200 rounded-lg p-4`}>
                <h4 className="font-bold text-sm text-blue-900 mb-3">
                  {assessmentCategories.internal.name}
                </h4>
                <p className="text-xs text-blue-600 mb-3">{assessmentCategories.internal.description}</p>
                <div className="space-y-2">
                  {assessments.map((assessment, idx) => {
                    const type = getAssessmentTypeFromTitle(assessment.title);
                    if (type.category === 'internal') {
                      return (
                        <div key={idx} className={`${type.color} border ${type.borderColor} rounded p-2 flex items-center gap-2`}>
                          <span className="text-lg">{type.icon}</span>
                          <div className="flex-1">
                            <div className={`text-xs font-bold ${type.textColor}`}>{type.name}</div>
                            <div className="text-xs text-muted-foreground">{type.description}</div>
                          </div>
                          <span className="text-xs font-bold text-slate-600">/{assessment.total_marks}</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>

              {/* External Assessment (Exam - 60%) */}
              <div className={`${assessmentCategories.external.color} border-2 border-red-200 rounded-lg p-4`}>
                <h4 className="font-bold text-sm text-red-900 mb-3">
                  {assessmentCategories.external.name}
                </h4>
                <p className="text-xs text-red-600 mb-3">{assessmentCategories.external.description}</p>
                <div className="bg-red-100 border border-red-300 rounded p-3 flex items-center gap-2">
                  <span className="text-2xl">📖</span>
                  <div className="flex-1">
                    <div className="text-xs font-bold text-red-700">FINAL EXAM</div>
                    <div className="text-xs text-red-600">End of Term Examination</div>
                  </div>
                  <span className="text-xs font-bold text-red-700">/60</span>
                </div>
              </div>

              {/* Grading Formula */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-4">
                <h4 className="font-bold text-sm text-purple-900 mb-3">
                  Final Grade Calculation
                </h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-blue-700">CA Total</span>
                      <span className="text-sm font-bold text-blue-900">40%</span>
                    </div>
                    <div className="text-xs text-slate-600">Sum of all Internal Assessments</div>
                  </div>
                  
                  <div className="text-center text-lg font-bold text-purple-600">+</div>
                  
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-red-700">Exam</span>
                      <span className="text-sm font-bold text-red-900">60%</span>
                    </div>
                    <div className="text-xs text-slate-600">Final Examination Score</div>
                  </div>
                  
                  <div className="text-center text-lg font-bold text-purple-600">=</div>
                  
                  <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-3 border-2 border-green-400">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-green-900">FINAL SCORE</span>
                      <span className="text-lg font-bold text-green-900">100%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Report Card Dialog - Only for fallback/legacy use */}
        <Dialog open={reportCardOpen && !isSecondarySchool() && !isPrimarySchool()} onOpenChange={setReportCardOpen}>
          <DialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto p-0 gap-0 bg-white">
            {selectedReportStudent && (
              <div className="p-8 text-slate-900">
                {/* Report Header */}
                <div className="flex items-center gap-6 border-b-2 border-slate-800 pb-6 mb-6 relative">
                  {/* Progress vs Final Watermark */}
                  {(() => {
                    const totalAssessments = selectedReportStudent.reportData?.subjects?.reduce((sum, subject) => 
                      sum + subject.assessments.length, 0) || 0;
                    const completedAssessments = selectedReportStudent.reportData?.subjects?.reduce((sum, subject) => 
                      sum + subject.assessments.filter(a => a.marks_obtained > 0).length, 0) || 0;
                    const isComplete = totalAssessments > 0 && completedAssessments === totalAssessments;
                    
                    return (
                      <div className="absolute top-0 right-0">
                        {isComplete ? (
                          <Badge className="bg-green-600 text-white px-4 py-2 text-xs">
                            ✓ OFFICIAL REPORT CARD
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-orange-400 text-orange-700 bg-orange-50 px-4 py-2 text-xs">
                            📊 PROGRESS REPORT
                          </Badge>
                        )}
                      </div>
                    );
                  })()}
                  
                  <img 
                    src="https://customer-assets.emergentagent.com/job_edu-roadmap-2/artifacts/hwv1fsz7_Untitled%20design_20251204_005232_0000.png" 
                    alt="School Logo" 
                    className="h-20 w-20 object-contain"
                  />
                  <div className="text-center flex-1">
                    <h1 className="text-3xl font-serif font-bold uppercase tracking-wider">{schoolName}</h1>
                    <p className="text-sm font-medium uppercase tracking-widest text-slate-500">Official Term Report</p>
                    <div className="mt-2 text-lg font-bold">Academic Year 2026</div>
                    <div className="flex items-center justify-center gap-2 text-sm text-blue-600 mt-1">
                      <Mail className="h-3 w-3" />
                      vureasaca25@gmail.com
                    </div>
                  </div>
                  <div className="h-20 w-20"></div>
                </div>

                {/* Student Details */}
                <div className="grid grid-cols-2 gap-x-12 gap-y-4 mb-6 text-sm">
                  <div className="flex justify-between border-b border-slate-200 pb-1">
                    <span className="font-bold text-slate-500">Student Name:</span>
                    <span className="font-bold text-lg">{selectedReportStudent.name}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-1">
                    <span className="font-bold text-slate-500">Year Level:</span>
                    <span className="font-medium">Year {selectedYear}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-1">
                    <span className="font-bold text-slate-500">Stream:</span>
                    <span className="font-medium">{selectedReportStudent.reportData?.stream || 'Not Assigned'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-1">
                    <span className="font-bold text-slate-500">Term:</span>
                    <span className="font-medium uppercase">{selectedTerm}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-1">
                    <span className="font-bold text-slate-500">Student ID:</span>
                    <span className="font-medium">{selectedReportStudent.id}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-1">
                    <span className="font-bold text-slate-500">Total Subjects:</span>
                    <span className="font-medium">{selectedReportStudent.reportData?.total_subjects || 0} subjects</span>
                  </div>
                </div>

                {/* Assessment Completion Status Banner */}
                {(() => {
                  const totalAssessments = selectedReportStudent.reportData?.subjects?.reduce((sum, subject) => 
                    sum + subject.assessments.length, 0) || 0;
                  const completedAssessments = selectedReportStudent.reportData?.subjects?.reduce((sum, subject) => 
                    sum + subject.assessments.filter(a => a.marks_obtained > 0).length, 0) || 0;
                  const completionPercentage = totalAssessments > 0 ? (completedAssessments / totalAssessments * 100) : 0;
                  const isComplete = totalAssessments > 0 && completedAssessments === totalAssessments;
                  
                  return (
                    <div className={`mb-6 p-4 rounded-lg border-2 ${
                      isComplete 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-orange-50 border-orange-300'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold">
                            {isComplete ? '✅ All Assessments Completed' : '⏳ Term In Progress'}
                          </span>
                        </div>
                        <span className="text-sm font-bold">
                          {completedAssessments} of {totalAssessments} assessments
                        </span>
                      </div>
                      <Progress value={completionPercentage} className="h-2" />
                      {!isComplete && (
                        <p className="text-xs text-orange-700 mt-2">
                          ⚠️ This is a progress report for teacher review only. Official report card will be available when all assessments are completed at term end.
                        </p>
                      )}
                      {isComplete && (
                        <p className="text-xs text-green-700 mt-2">
                          ✓ This official report card is ready to be printed and shared with parents.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Attendance Summary Card */}
                <div className="mb-8 border-2 border-blue-200 rounded-lg overflow-hidden bg-gradient-to-r from-blue-50 to-sky-50">
                  <div className="bg-blue-600 text-white px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      <h3 className="font-bold text-lg">Attendance Summary</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold">{selectedReportStudent.attendanceData.percentage}%</span>
                      <Badge className={`${
                        selectedReportStudent.attendanceData.percentage >= 95 ? 'bg-green-600' :
                        selectedReportStudent.attendanceData.percentage >= 85 ? 'bg-yellow-600' :
                        'bg-red-600'
                      }`}>
                        {selectedReportStudent.attendanceData.percentage >= 95 ? 'Excellent' :
                         selectedReportStudent.attendanceData.percentage >= 85 ? 'Good' : 'At Risk'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {selectedReportStudent.attendanceData.present}
                        </div>
                        <div className="text-xs text-slate-500 uppercase font-medium">Present</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {selectedReportStudent.attendanceData.absent}
                        </div>
                        <div className="text-xs text-slate-500 uppercase font-medium">Absent</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                        <div className="text-2xl font-bold text-orange-600">
                          {selectedReportStudent.attendanceData.late}
                        </div>
                        <div className="text-xs text-slate-500 uppercase font-medium">Late</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {selectedReportStudent.attendanceData.total}
                        </div>
                        <div className="text-xs text-slate-500 uppercase font-medium">Total Days</div>
                      </div>
                    </div>

                    {/* Attendance Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-medium text-slate-600">
                        <span>Attendance Rate</span>
                        <span>{selectedReportStudent.attendanceData.percentage}% of {selectedReportStudent.attendanceData.total} days</span>
                      </div>
                      <Progress 
                        value={selectedReportStudent.attendanceData.percentage} 
                        className="h-3"
                      />
                    </div>
                  </div>
                </div>

                {/* Academic Table with All Subjects from Multiple Teachers */}
                <div className="mb-8">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-100 border-y-2 border-slate-800 text-slate-700">
                        <th className="text-left py-2 px-2">Subject</th>
                        <th className="text-left py-2 px-2">Teacher</th>
                        <th className="text-center py-2 px-2 w-20">Marks</th>
                        <th className="text-center py-2 px-2 w-16">Percentage</th>
                        <th className="text-center py-2 px-2 w-12 font-bold">Grade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedReportStudent.reportData?.subjects?.map((subject, index) => (
                        <tr key={index} className="border-b border-slate-200 hover:bg-slate-50">
                          <td className="py-3 px-2 font-medium">{subject.subject}</td>
                          <td className="py-3 px-2 text-slate-600 text-xs">{subject.teacher_name}</td>
                          <td className="py-3 px-2 text-center text-slate-500">
                            {subject.total_marks_obtained || 0}/{subject.total_marks_possible || 0}
                          </td>
                          <td className="py-3 px-2 text-center font-medium">
                            {(subject.percentage || 0).toFixed(1)}%
                          </td>
                          <td className="py-3 px-2 text-center font-bold text-lg">
                            <span className={`inline-block px-2 py-1 rounded ${
                              subject.grade === 'A' ? 'bg-green-100 text-green-800' :
                              subject.grade === 'B' ? 'bg-blue-100 text-blue-800' :
                              subject.grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                              subject.grade === 'D' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {subject.grade}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {/* Overall Summary Row */}
                      <tr className="bg-blue-50 border-t-2 border-blue-800 font-bold">
                        <td className="py-3 px-2" colSpan="2">OVERALL PERFORMANCE</td>
                        <td className="py-3 px-2 text-center">{selectedReportStudent.reportData?.total_subjects || 0} Subjects</td>
                        <td className="py-3 px-2 text-center text-lg">
                          {(selectedReportStudent.reportData?.overall_percentage || 0).toFixed(1)}%
                        </td>
                        <td className="py-3 px-2 text-center text-xl text-blue-800">
                          GPA: {(selectedReportStudent.reportData?.overall_gpa || 0).toFixed(1)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Footer Stats & Comments */}
                <div className="grid grid-cols-2 gap-12">
                  <div className="space-y-6">
                    <div className="bg-slate-50 p-4 rounded border border-slate-200">
                      <h4 className="font-bold text-xs uppercase text-slate-500 mb-2">Principal&apos;s Comment</h4>
                      <p className="text-sm font-serif italic">
                        &quot;{(() => {
                          const overallPercentage = selectedReportStudent.reportData?.overall_percentage || 0;
                          const overallGPA = selectedReportStudent.reportData?.overall_gpa || 0;
                          const attendance = selectedReportStudent.attendanceData.percentage;
                          
                          if (overallGPA >= 3.5 && attendance >= 95) {
                            return "An outstanding term showing excellence across all subjects and exceptional attendance. Continue this exemplary performance!";
                          } else if (overallGPA >= 3.0 && attendance >= 85) {
                            return "Good overall academic performance across all subjects. Maintain consistent effort and regular attendance to achieve greater success.";
                          } else if (overallGPA >= 3.0 && attendance < 85) {
                            return "Good academic results across subjects, but attendance needs improvement. Regular school attendance is essential for continued success.";
                          } else if (overallGPA >= 2.0 && attendance >= 85) {
                            return "Satisfactory performance overall. Focus on strengthening weaker subjects and seeking help from teachers when needed.";
                          } else if (overallGPA >= 2.0) {
                            return "Pass level achieved but improvement needed in both academics and attendance. Increased effort and regular attendance will lead to better results.";
                          } else {
                            return "Significant improvement required across multiple subjects. Immediate focus on regular attendance, assignment completion, and extra support needed. Parent conference scheduled.";
                          }
                        })()}&quot;
                      </p>
                    </div>

                    <div className="pt-8 border-t border-slate-800 mt-8">
                      <p className="text-center font-bold text-sm uppercase">Principal&apos;s Signature</p>
                      <div className="h-24 flex items-center justify-center gap-4">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_edu-roadmap-2/artifacts/xkdoeu40_IMG_20251117_142906.jpg" 
                          alt="Principal Signature" 
                          className="h-16 object-contain"
                        />
                        <img 
                          src="https://customer-assets.emergentagent.com/job_edu-roadmap-2/artifacts/fsyov6gs_20220527_115408~2.jpg" 
                          alt="School Stamp" 
                          className="h-20 object-contain opacity-80 -ml-8 rotate-12"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                      <span className="font-bold text-sm uppercase">Total Subjects</span>
                      <span className="font-bold text-xl">{selectedReportStudent.reportData?.total_subjects || 0} Subjects</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                      <span className="font-bold text-sm uppercase">Term Average</span>
                      <span className="font-bold text-xl text-primary">
                        {(selectedReportStudent.reportData?.overall_percentage || 0).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-slate-200 pb-2 bg-purple-50 px-2 -mx-2 rounded">
                      <span className="font-bold text-sm uppercase text-purple-900">Overall GPA</span>
                      <span className="font-bold text-xl text-purple-700">
                        {(selectedReportStudent.reportData?.overall_gpa || 0).toFixed(1)}
                      </span>
                    </div>
                    <div className="pt-8 border-t border-slate-800 mt-10">
                      <p className="text-center font-bold text-sm uppercase">Class Teacher</p>
                      <p className="text-center text-sm text-slate-600 mt-2">{currentUser?.name}</p>
                    </div>
                  </div>
                </div>

                {/* Print Buttons - Different for Teachers vs Final Report */}
                <div className="mt-6 flex justify-center gap-4">
                  {(() => {
                    const totalAssessments = selectedReportStudent.reportData?.subjects?.reduce((sum, subject) => 
                      sum + subject.assessments.length, 0) || 0;
                    const completedAssessments = selectedReportStudent.reportData?.subjects?.reduce((sum, subject) => 
                      sum + subject.assessments.filter(a => a.marks_obtained > 0).length, 0) || 0;
                    const isComplete = totalAssessments > 0 && completedAssessments === totalAssessments;
                    
                    return (
                      <>
                        {/* Teacher can always print for their own review */}
                        <Button 
                          onClick={() => window.print()} 
                          variant="outline"
                          className="gap-2"
                        >
                          <Printer className="h-4 w-4" />
                          Print Progress Report (Teacher Review)
                        </Button>
                        
                        {/* Print for Parents - Only enabled when term is complete */}
                        <Button 
                          onClick={() => window.print()} 
                          disabled={!isComplete}
                          className={`gap-2 ${!isComplete ? 'opacity-50 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                          title={!isComplete ? 'Available when all assessments are completed' : 'Ready to share with parents'}
                        >
                          <Printer className="h-4 w-4" />
                          {isComplete ? '✓ Print Official Report for Parents' : '🔒 Print for Parents (Locked)'}
                        </Button>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Primary School Report Card */}
      {isPrimarySchool() && (
        <PrimaryReportCard
          isOpen={reportCardOpen && selectedStudentForReport !== null}
          onClose={() => {
            setReportCardOpen(false);
            setSelectedStudentForReport(null);
          }}
          student={selectedStudentForReport}
          classInfo={selectedClass}
          assessmentMarks={{...assessmentMarks, ...Object.fromEntries(
            Object.entries(examMarks).map(([key, value]) => [`${key.split('-')[0]}-exam`, value])
          )}}
          reportData={selectedStudentForReport?.reportData}
          schoolName={schoolName}
          schoolAddress={schoolData.address || "Penama Province, Vanuatu"}
          schoolPhone={schoolData.phone || ""}
          schoolId={schoolData.school_id || currentUser?.school_id || ""}
          teacherName={currentUser?.name}
          principalName={schoolData.principal_name || "Principal"}
          term={selectedTerm}
          year={new Date().getFullYear()}
          absences={selectedStudentForReport?.attendanceData?.absent || 0}
        />
      )}
      
      {/* Secondary School Report Card */}
      {isSecondarySchool() && selectedReportStudent && (
        <SecondaryReportCard
          isOpen={reportCardOpen && selectedReportStudent !== null}
          onClose={() => {
            setReportCardOpen(false);
            setSelectedReportStudent(null);
          }}
          student={selectedReportStudent}
          classInfo={{
            name: `Year ${selectedYear} ${selectedSubject}`,
            year: selectedYear,
            subject: selectedSubject
          }}
          grades={selectedReportStudent.gradesForReport || []}
          schoolName={schoolName}
          term={selectedTerm}
          year={new Date().getFullYear()}
          studentYear={parseInt(selectedYear)}
          teacherName={currentUser?.name}
          principalName={schoolData.principal_name || "Principal"}
          schoolAddress={(() => {
            const addr = schoolData.address || '';
            if (schoolName?.toLowerCase().includes("patrick") || schoolName?.toLowerCase().includes("spc")) {
              return {
                poBox: "PO BOX 19",
                location: addr || "Nawolu, Vureas, North East Ambae",
                country: "VANUATU",
                email: "spc.secretaryoffice@gmail.com"
              };
            }
            return {
              poBox: "",
              location: addr || "Penama Province",
              country: "VANUATU",
              email: ""
            };
          })()}
          attendance={selectedReportStudent?.reportData?.attendance || null}
          onSubjectToggle={async (studentId, subject, year, active) => {
            try {
              await apiService.toggleStudentSubject(studentId, subject, year, active);
              toast.success(`${subject} ${active ? 'enabled' : 'disabled'} for report card`);
            } catch (error) {
              console.error('Error toggling subject:', error);
              toast.error('Failed to update subject');
            }
          }}
          onStreamChange={async (studentId, stream, year) => {
            toast.info(`Stream changed to ${stream}`);
          }}
        />
      )}

      {/* Batch Grade Entry Dialog */}
      <Dialog open={batchEntryOpen} onOpenChange={setBatchEntryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ClipboardList className="h-6 w-6 text-green-600" />
              Batch Grade Entry - Year {selectedYear} {selectedSubject}
            </DialogTitle>
            <DialogDescription>
              Quickly enter grades for all {students.length} students at once. Select assessment type, strand, and enter marks.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Assessment Type and Strand Selector */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Assessment Type */}
              <div>
                <label className="text-sm font-medium mb-2 block">Assessment Type</label>
                <Select value={batchEntryType} onValueChange={(val) => {
                  setBatchEntryType(val);
                  setBatchGrades({});
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {batchEntryType !== 'exam' && (
                      <>
                        {/* Primary School - Subject-specific assessment types from Vanuatu Syllabus */}
                        {isPrimarySchool() && (
                          <>
                            {/* English Assessment Types */}
                            {(selectedSubject === 'English' || !selectedSubject) && (
                              <>
                                <SelectItem value="oral-presentation" className="text-blue-700">🎤 Oral Presentation</SelectItem>
                                <SelectItem value="debate" className="text-blue-700">💬 Debate</SelectItem>
                                <SelectItem value="role-play" className="text-blue-700">🎭 Role Play</SelectItem>
                                <SelectItem value="story-telling" className="text-blue-700">📖 Story Telling</SelectItem>
                                <SelectItem value="dialogue" className="text-blue-700">🗣️ Dialogue</SelectItem>
                                <SelectItem value="oral-reading" className="text-blue-700">📚 Oral Reading</SelectItem>
                                <SelectItem value="comprehension" className="text-blue-700">📝 Comprehension</SelectItem>
                                <SelectItem value="cloze-activity" className="text-blue-700">✍️ Cloze Activity</SelectItem>
                                <SelectItem value="book-review" className="text-blue-700">📕 Book Review</SelectItem>
                                <SelectItem value="spelling" className="text-blue-700">🔤 Spelling</SelectItem>
                                <SelectItem value="grammar" className="text-blue-700">📋 Grammar</SelectItem>
                                <SelectItem value="handwriting" className="text-blue-700">✒️ Handwriting</SelectItem>
                                <SelectItem value="writing-draft" className="text-blue-700">📄 Writing Draft</SelectItem>
                                <SelectItem value="visual-text" className="text-blue-700">🖼️ Visual Text</SelectItem>
                              </>
                            )}
                            
                            {/* Mathematics Assessment Types */}
                            {(selectedSubject === 'Mathematics' || !selectedSubject) && (
                              <>
                                <SelectItem value="mental-calculation" className="text-green-700">🧮 Mental Calculation</SelectItem>
                                <SelectItem value="worksheet" className="text-green-700">📋 Worksheet</SelectItem>
                                <SelectItem value="investigation" className="text-green-700">🔍 Investigation</SelectItem>
                                <SelectItem value="practical-activity" className="text-green-700">🔧 Practical Activity</SelectItem>
                                <SelectItem value="measurement" className="text-green-700">📏 Measurement</SelectItem>
                                <SelectItem value="geometric-drawing" className="text-green-700">📐 Geometric Drawing</SelectItem>
                                <SelectItem value="operations" className="text-green-700">➕ Operations</SelectItem>
                                <SelectItem value="number-facts" className="text-green-700">🔢 Number Facts</SelectItem>
                                <SelectItem value="tables-test" className="text-green-700">✖️ Tables Test</SelectItem>
                                <SelectItem value="graph-chart" className="text-green-700">📊 Graph/Chart</SelectItem>
                                <SelectItem value="pattern-work" className="text-green-700">🔷 Pattern Work</SelectItem>
                              </>
                            )}
                            
                            {/* Science Assessment Types */}
                            {(selectedSubject === 'Science' || !selectedSubject) && (
                              <>
                                <SelectItem value="observation" className="text-purple-700">👁️ Observation</SelectItem>
                                <SelectItem value="experiment" className="text-purple-700">🧪 Experiment</SelectItem>
                                <SelectItem value="diagram" className="text-purple-700">📊 Diagram</SelectItem>
                                <SelectItem value="model" className="text-purple-700">🏗️ Model</SelectItem>
                                <SelectItem value="science-sketch" className="text-purple-700">✏️ Science Sketch</SelectItem>
                                <SelectItem value="food-chain" className="text-purple-700">🔗 Food Chain</SelectItem>
                                <SelectItem value="predictions" className="text-purple-700">🎯 Predictions</SelectItem>
                                <SelectItem value="safety-skills" className="text-purple-700">⚠️ Safety Skills</SelectItem>
                              </>
                            )}
                            
                            {/* General Assessment Types for all subjects */}
                            <SelectItem value="quiz" className="text-gray-700">📝 Quiz</SelectItem>
                            <SelectItem value="test" className="text-gray-700">📋 Test</SelectItem>
                            <SelectItem value="project" className="text-gray-700">📊 Project</SelectItem>
                            <SelectItem value="homework" className="text-gray-700">📚 Homework</SelectItem>
                            <SelectItem value="presentation" className="text-gray-700">🎤 Presentation</SelectItem>
                            <SelectItem value="observation" className="text-gray-700">👁️ Observation</SelectItem>
                          </>
                        )}
                        
                        {/* Secondary School - General assessment types */}
                        {!isPrimarySchool() && (
                          <>
                            <SelectItem value="quiz">📝 Quiz</SelectItem>
                            <SelectItem value="test">📋 Test</SelectItem>
                            <SelectItem value="project">📊 Project</SelectItem>
                            <SelectItem value="homework">📚 Homework</SelectItem>
                            <SelectItem value="practical">🔧 Practical</SelectItem>
                            <SelectItem value="exercise">✏️ Exercise</SelectItem>
                            <SelectItem value="assignment">📄 Assignment</SelectItem>
                            <SelectItem value="presentation">🎤 Presentation</SelectItem>
                            <SelectItem value="observation">👁️ Observation</SelectItem>
                          </>
                        )}
                      </>
                    )}
                    <SelectItem value="exam">📋 End of Term Exam (60%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Strand Selector - Only show for CA assessments, not exam */}
              {batchEntryType !== 'exam' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Strand (for CA 40%)</label>
                  <Select value={batchEntryStrand} onValueChange={(val) => {
                    setBatchEntryStrand(val);
                    setBatchGrades({});
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Strand" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const strandInfo = getCurrentStrandInfo();
                        if (strandInfo?.strands) {
                          return strandInfo.strands.map((strand, idx) => (
                            <SelectItem key={`strand${idx + 1}`} value={`strand${idx + 1}`}>
                              S{idx + 1}: {strand.length > 25 ? strand.substring(0, 25) + '...' : strand}
                            </SelectItem>
                          ));
                        }
                        // Default strands if subject not found
                        return (
                          <>
                            <SelectItem value="strand1">Strand 1</SelectItem>
                            <SelectItem value="strand2">Strand 2</SelectItem>
                            <SelectItem value="strand3">Strand 3</SelectItem>
                            <SelectItem value="strand4">Strand 4</SelectItem>
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Max Marks Info */}
              <div className="flex items-end">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 w-full">
                  <p className="text-xs text-blue-600 font-medium">
                    {batchEntryType === 'exam' ? 'Exam contributes to 60%' : `CA Assessment → adds to 40%`}
                  </p>
                  <p className="text-lg font-bold text-blue-700">
                    Max: {batchTotalScore ? parseInt(batchTotalScore) : (batchEntryType === 'exam' ? examTotalMarks : 
                          isPrimarySchool() ? primaryAssessmentMaxMarks[batchEntryType] || 10 :
                          assessmentMaxMarks[batchEntryType] || 10)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Custom Assessment Name and Total Score - NEW */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
              <div>
                <label className="text-sm font-medium mb-2 block text-purple-800">
                  Custom Assessment Name (Optional)
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Unit 3 Test, Chapter Review, Weekly Quiz..."
                  value={batchCustomName}
                  onChange={(e) => setBatchCustomName(e.target.value)}
                  className="bg-white"
                  data-testid="batch-custom-name"
                />
                <p className="text-xs text-purple-600 mt-1">
                  Leave empty to use the selected assessment type name
                </p>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block text-purple-800">
                  Total Score (Optional)
                </label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  placeholder="e.g., 20, 50, 100..."
                  value={batchTotalScore}
                  onChange={(e) => setBatchTotalScore(e.target.value)}
                  className="bg-white"
                  data-testid="batch-total-score"
                />
                <p className="text-xs text-purple-600 mt-1">
                  Leave empty to use default max marks for the assessment type
                </p>
              </div>
            </div>
            
            {/* Assessment Label */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800">
                {batchEntryType === 'exam' 
                  ? `📋 ${batchCustomName || 'End of Term Exam'} - ${selectedTerm} (60% of Total)`
                  : `${batchCustomName || (batchEntryType.charAt(0).toUpperCase() + batchEntryType.slice(1).replace(/-/g, ' '))} - ${batchEntryStrand.replace('strand', 'Strand ')} - ${selectedTerm} (CA 40%)`
                }
                {batchTotalScore && <span className="ml-2 text-green-600">| Total: {batchTotalScore} marks</span>}
              </p>
            </div>

            {/* Student Grade Entry Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-green-50">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Student Name</TableHead>
                    <TableHead className="w-32 text-center">Current</TableHead>
                    <TableHead className="w-40 text-center">New Mark</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student, idx) => {
                    const currentMark = batchEntryType === 'exam' 
                      ? getExamMark(student.id) 
                      : getAssessmentMark(student.id, batchEntryType);
                    // Use custom total score if provided, otherwise use defaults
                    const maxMark = batchTotalScore ? parseInt(batchTotalScore) : (
                                   batchEntryType === 'exam' ? examTotalMarks :
                                   isPrimarySchool() ? primaryAssessmentMaxMarks[batchEntryType] || 10 :
                                   assessmentMaxMarks[batchEntryType] || 20);
                    
                    return (
                      <TableRow key={student.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{student.name}</TableCell>
                        <TableCell className="text-center">
                          {currentMark !== null ? (
                            <Badge variant="outline" className="bg-blue-50">
                              {currentMark}/{maxMark}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={0}
                            max={maxMark}
                            placeholder={`0-${maxMark}`}
                            value={batchGrades[student.id] ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setBatchGrades(prev => ({
                                ...prev,
                                [student.id]: val === '' ? '' : Math.min(Math.max(0, parseInt(val) || 0), maxMark)
                              }));
                            }}
                            className="h-8 text-center"
                            data-testid={`batch-grade-${student.id}`}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Quick Fill Options */}
            <div className="flex gap-2 items-center p-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-sm font-medium text-amber-800">Quick Fill:</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const maxMark = batchEntryType === 'exam' ? examTotalMarks :
                                 isPrimarySchool() ? primaryAssessmentMaxMarks[batchEntryType] || 10 :
                                 assessmentMaxMarks[batchEntryType] || 20;
                  const filled = {};
                  students.forEach(s => { filled[s.id] = maxMark; });
                  setBatchGrades(filled);
                }}
              >
                All Max
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const maxMark = batchEntryType === 'exam' ? examTotalMarks :
                                 isPrimarySchool() ? primaryAssessmentMaxMarks[batchEntryType] || 10 :
                                 assessmentMaxMarks[batchEntryType] || 20;
                  const filled = {};
                  students.forEach(s => { filled[s.id] = Math.round(maxMark * 0.8); });
                  setBatchGrades(filled);
                }}
              >
                All 80%
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const maxMark = batchEntryType === 'exam' ? examTotalMarks :
                                 isPrimarySchool() ? primaryAssessmentMaxMarks[batchEntryType] || 10 :
                                 assessmentMaxMarks[batchEntryType] || 20;
                  const filled = {};
                  students.forEach(s => { filled[s.id] = Math.round(maxMark * 0.5); });
                  setBatchGrades(filled);
                }}
              >
                All 50%
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setBatchGrades({})}
              >
                Clear All
              </Button>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setBatchEntryOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700 gap-2"
                onClick={async () => {
                  // Build grades array for API
                  const gradesToSave = Object.entries(batchGrades)
                    .filter(([_, value]) => value !== '' && value !== null)
                    .map(([studentId, value]) => ({
                      student_id: studentId,
                      raw_marks: parseFloat(value)
                    }));
                  
                  if (gradesToSave.length === 0) {
                    toast.error('No grades to save');
                    return;
                  }
                  
                  // Determine max marks based on assessment type or custom total score
                  const maxMark = batchTotalScore ? parseInt(batchTotalScore) : (
                                 batchEntryType === 'exam' ? examTotalMarks :
                                 isPrimarySchool() ? primaryAssessmentMaxMarks[batchEntryType] || 10 :
                                 assessmentMaxMarks[batchEntryType] || 10);
                  
                  // Get the display name for the assessment
                  const assessmentDisplayName = batchCustomName || batchEntryType;
                  
                  try {
                    // Save to database via API for Principal sync
                    if (selectedClass?.id) {
                      // Pass strand for CA assessments, null for exam
                      const strandToSave = batchEntryType === 'exam' ? null : batchEntryStrand;
                      
                      await apiService.quickGradeEntry(
                        selectedClass.id,
                        batchEntryType,
                        selectedTerm,
                        maxMark,
                        gradesToSave,
                        strandToSave,
                        batchCustomName // Pass custom name to API
                      );
                    }
                    
                    // Also update local state for immediate UI feedback
                    gradesToSave.forEach(({ student_id, raw_marks }) => {
                      if (batchEntryType === 'exam') {
                        updateExamMark(student_id, raw_marks);
                      } else {
                        updateAssessmentMark(student_id, batchEntryType, raw_marks);
                      }
                    });
                    
                    // Build success message with custom name and strand info
                    const displayName = batchCustomName ? `"${batchCustomName}"` : batchEntryType;
                    const strandLabel = batchEntryType !== 'exam' ? ` (${batchEntryStrand.replace('strand', 'Strand ')})` : '';
                    const categoryLabel = batchEntryType === 'exam' ? '(60%)' : '→ CA (40%)';
                    
                    toast.success(`✅ Saved ${gradesToSave.length} ${displayName}${strandLabel} grades ${categoryLabel}`);
                    setBatchEntryOpen(false);
                    setBatchGrades({});
                    setBatchCustomName('');
                    setBatchTotalScore('');
                  } catch (error) {
                    console.error('Error saving batch grades:', error);
                    toast.error('Failed to save grades to database. Please try again.');
                  }
                }}
                disabled={Object.keys(batchGrades).filter(k => batchGrades[k] !== '').length === 0}
              >
                <Save className="h-4 w-4" />
                Save {Object.keys(batchGrades).filter(k => batchGrades[k] !== '').length} Grades
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
